import { useLanguage } from "../../context/LanguageContext";
import { defaultSimulationYear, operatingStartYear } from "../../utils/simulationYears";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FeasibilityShell from "./FeasibilityShell";
import { CostingMethodSelector, SimplifiedCostingCard, costingModeOf } from "./CostingMethod";
import { useHistory } from "../../context/HistoryContext";
import { DEFAULT_CONFIG } from "../../components/config/defaultConfig";
import {
  DEFAULT_DISPATCH_FORESIGHT_PROVENANCE,
  DISPATCH_FORESIGHT_MIN_ANNUAL_DAYS,
  DEFAULT_FORWARD_CURVE,
  DEFAULT_MERCHANT_REVENUE_PROJECTION,
  FORWARD_CURVE_SCENARIOS,
  FORWARD_LEVEL_INPUT_MODES,
  FORWARD_REDISPATCH_GRANULARITIES,
  MERCHANT_REVENUE_PROJECTION_MODES,
  dispatchForesightBasisFor,
  dispatchForesightProvenanceIssue,
  foresightBatteryMismatch,
  frozenForesightOption,
  normalizeMerchantRevenueProjection,
  parseDispatchForesightMeasurement,
} from "../../components/config/merchantRevenueProjection";
import {
  HOUSE_FORWARD_PATH,
  buildHouseForwardPath,
  housePathYearsBeforeFirstAnchor,
} from "../../components/config/houseForwardPath";
import { availabilityModellingIssue } from "../../components/config/availabilityModelling";
import { isBtmSettlement } from "../../components/config/btm";
import {
  DEFAULT_EQUIPMENT_COST_OVERRIDE,
  parseOverrideEur,
  pruneStaleOverrides,
} from "../../components/config/equipmentCostOverride";
import { normalizeMarketZone } from "../../utils/marketSupport";
import { useFile } from "../../context/FileContext";
import {
  getBatteryDetails,
  fetchDamAnnualMean,
  getCaptureRatio,
  getInverterDetails,
  getModuleDetails,
  measureCaptureRatio,
} from "../../services/api";
import {
  CONSTRUCTION_DRAWDOWN_MODE_OPTIONS,
  DEBT_SERVICE_FREQUENCY_OPTIONS,
  DEBT_SIZING_METHOD_OPTIONS,
  DEFAULT_PROJECT_DEBT,
  DSRA_FUNDING_METHOD_OPTIONS,
  FACILITY_STATUS_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  INTEREST_DURING_CONSTRUCTION_TREATMENT_OPTIONS,
  INTEREST_TYPE_OPTIONS,
  MORATORIUM_INTEREST_TREATMENT_OPTIONS,
  REFINANCING_ASSUMPTION_OPTIONS,
  SECURITY_PACKAGE_STATUS_OPTIONS,
  normalizeProjectDebtConfig,
} from "../../components/config/projectDebt";


function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}
function SubSection({ title, description, children, action }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        background: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.015)",
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
          </Stack>
          {description ? (
            <Typography
              sx={{ fontSize: "0.845rem", lineHeight: 1.55, color: "text.secondary", mt: 0.35 }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        {children}
      </Stack>
    </Box>
  );
}

const economicsInputLabelSx = {
  color: "primary.main",
  fontWeight: 600,
  letterSpacing: "0.02em",
  fontSize: "0.95rem",
  whiteSpace: "normal",
  lineHeight: 1.2,
  maxWidth: "100%",
  "&.Mui-focused": { color: "primary.main" },
};

const economicsFieldSx = {
  "& .MuiInputBase-root": { minHeight: 58, borderRadius: 2 },
  "& .MuiInputBase-input": { py: 1.35 },
  "& .MuiFormHelperText-root": {
    mt: 0.6,
    lineHeight: 1.5,
    // Same readable size as the field hints on the BESS page.
    fontSize: "0.78rem",
    color: "text.secondary",
  },
};

const EURO = "\u20AC";

const STANDALONE_BESS_ZERO_INVESTMENT_KEYS = [
  "moduleEurPerWdc",
  "inverterEurPerWdc",
  "gridInterconnectionEurPerKwp",
  "designEngineeringEurPerKwp",
  "moduleRecurringPctYear",
  "inverterRecurringPctYear",
  "mountingStructuresEurPerKwp",
  "commissioningTestsEurPerKwp",
  "cablingConnectionsEurPerKwp",
];

const STANDALONE_BESS_ZERO_INVESTMENT_KEY_SET = new Set(
  STANDALONE_BESS_ZERO_INVESTMENT_KEYS
);

// PV Asset Status == "existing" (sunk asset / retrofit): the backend
// structurally EXCLUDES these two fields from CAPEX (see
// BESS_feasibility read_config.pv_is_existing), so the UI locks them as
// read-only with a 0 display. Stored values are preserved and restored
// when switching back to "new". Only these two are ignored by the
// backend — all other investment-cost fields still apply (BESS/site
// costs), so they stay editable.
const SUNK_PV_EXCLUDED_INVESTMENT_KEYS = new Set([
  "moduleEurPerWdc",
  "inverterEurPerWdc",
]);
const SUNK_PV_HELPER_TEXT =
  "Excluded from CAPEX: PV Asset Status is 'Existing plant — sunk asset'. Your value is kept and applies again when the status returns to 'New build'.";

function normalizeEconomicsNumberString(raw) {
  if (raw === "" || raw == null) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(4).replace(/\.?0+$/, "");
}

function LabeledField({
  InputLabelProps,
  type,
  value,
  onChange,
  helperText,
  placeholder,
  required,
  sx,
  select,
  allowNegative = false,
  children,
  ...props
}) {
  const isNumber = type === "number";
  const isSelect = !!select;
  const [localValue, setLocalValue] = useState(
    value === undefined || value === null ? "" : String(value)
  );

  useEffect(() => {
    setLocalValue(value === undefined || value === null ? "" : String(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (isSelect) {
      setLocalValue(raw);
      onChange?.(e);
      return;
    }

    const normalized = raw.replace(",", ".");
    const numberPattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (isNumber && !numberPattern.test(normalized)) return;
    setLocalValue(normalized);
  };

  const handleBlur = () => {
    if (isSelect) return;
    const next = isNumber ? normalizeEconomicsNumberString(localValue) : localValue;
    setLocalValue(next);
    onChange?.({ target: { value: next } });
  };

  return (
    <TextField
      {...props}
      select={select}
      required={required}
      type={isNumber ? "text" : type}
      inputMode={isNumber ? "decimal" : undefined}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      helperText={helperText}
      sx={{ ...economicsFieldSx, ...sx }}
      InputLabelProps={{
        shrink: true,
        ...InputLabelProps,
        sx: { ...economicsInputLabelSx, ...(InputLabelProps?.sx || {}) },
      }}
    >
      {children}
    </TextField>
  );
}


/**
 * Dispatch realism (capture ratio): the basis, chosen - not typed.
 *
 * The measurement is performed by the PLATFORM (GET/POST /dam/capture-ratio),
 * so nobody runs a script and nobody retypes a number. Two bases are offered:
 * this project's own battery, which is what a lender should be given because
 * the ratio is a property of the battery as much as of the forecast, and the
 * market's reference measurement, which is instant and is disclosed in the
 * report as not being this project's battery.
 *
 * Whatever is chosen is FROZEN into the project with its window and date. A
 * later re-measurement never moves a project that has already accepted one -
 * a figure supporting a financing decision has to be the same figure when the
 * decision is reviewed.
 *
 * The paste box survives behind "Advanced" for an offline/no-database
 * deployment, where the CLI's --emit-config block is the only route in.
 */
export function DispatchForesightBasis({
  market,
  projectId,
  factor,
  provenance,
  onApply,
  onClear,
  onFactorChange,
  projectBatteryKw,
  projectBatteryKwh,
}) {
  const [available, setAvailable] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [measureError, setMeasureError] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [pasted, setPasted] = useState("");
  // "Manual assumption" and "No haircut (1.00)" cannot be told apart from the
  // stored config alone: both are an assumption, and the only difference is
  // whether the number happens to be 1. Deriving the selection from the factor
  // therefore TRAPPED the user - once "No haircut" set the factor to 1,
  // choosing "Manual" left the factor at 1 and the select snapped straight
  // back. The user's choice is its own fact, so it is its own state.
  const [manualChosen, setManualChosen] = useState(false);

  const parsedPaste = useMemo(() => parseDispatchForesightMeasurement(pasted), [pasted]);
  const current = provenance || DEFAULT_DISPATCH_FORESIGHT_PROVENANCE;
  const isMeasured = String(current.basis || "") === "measured";
  const issue = useMemo(
    () => dispatchForesightProvenanceIssue(factor, current),
    [factor, current]
  );

  const reload = useCallback(async () => {
    if (!market) return;
    setLoading(true);
    setLoadError("");
    try {
      setAvailable(await getCaptureRatio({ market, projectId }));
    } catch (err) {
      // A missing measurement is not an error the user caused, and it must not
      // block the page: the manual basis stays fully usable.
      setAvailable(null);
      setLoadError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [market, projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Which basis the project is on. A measured basis is fully determined by what
  // the project stores; between the two assumption bases, a factor other than 1
  // can only have been typed, and at exactly 1 the user's own choice decides.
  const basis = dispatchForesightBasisFor({
    factor,
    provenance: current,
    manualChosen,
  });

  const projectMeasurement = available?.project || null;
  const referenceMeasurement = available?.reference || null;

  // A measurement the platform will not vouch for is NOT applied, however the
  // user got here. This is the fix for the 40-day / 0.2612 incident: the
  // measurement was produced, the report correctly refused to call it a
  // measurement, and it was applied anyway - taking a project NPV from
  // +222,209 to -239,976 on one button press.
  const applyMeasurement = (payload) => {
    if (!payload) return false;
    if (!payload.usable) {
      setMeasureError(
        `Not applied. ${payload.unusable_reason} ` +
          "The project's current basis is unchanged."
      );
      return false;
    }
    setMeasureError("");
    onApply(payload.factor, payload.provenance);
    return true;
  };

  const handleBasisChange = (event) => {
    const next = event.target.value;
    // The applied (frozen) measurement is already in force.
    if (next === "applied") return;
    if (next === "project") {
      setManualChosen(false);
      applyMeasurement(projectMeasurement);
      return;
    }
    if (next === "reference") {
      setManualChosen(false);
      applyMeasurement(referenceMeasurement);
      return;
    }
    // Manual keeps whatever number is applied but withdraws the citation;
    // "none" is the neutral 1.00 the whole model defaults to.
    if (next === "manual") {
      setManualChosen(true);
      onClear();
      return;
    }
    if (next === "none") {
      setManualChosen(false);
      onClear();
      onFactorChange(1);
    }
  };

  const runMeasurement = async () => {
    setMeasuring(true);
    setMeasureError("");
    try {
      const result = await measureCaptureRatio({ market, projectId, scope: "project" });
      // Stored either way - a measurement of a poor forecast is still evidence,
      // and the history is the point - but only applied if it is usable.
      applyMeasurement(result?.measurement);
      await reload();
    } catch (err) {
      setMeasureError(String(err?.message || err));
    } finally {
      setMeasuring(false);
    }
  };

  // WHAT THE CARD MUST SHOW: what the PROJECT applies, not what the server
  // happens to hold. These differ - a project can be frozen on a measurement
  // taken elsewhere (pasted from the CLI, or taken before a newer server
  // measurement), and showing the server's row while the report uses the
  // project's is the same class of mistake as a warning that does not block.
  const appliedCard = useMemo(() => {
    if (!isMeasured) return null;
    const source = String(current.forecastSource || "");
    return {
      factor: Number(factor),
      provenance: current,
      scope: String(current.scope || "reference"),
      bess_description: current.bessDescription || "",
      usable: true,
      unusable_reason: "",
      warnings: [],
      window_is_annual:
        Number(current.daysEvaluated || 0) >= DISPATCH_FORESIGHT_MIN_ANNUAL_DAYS,
      forecast_source: source,
      forecast_source_note:
        source === "walkforward"
          ? "Walk-forward out-of-sample forecast: each fold refits only on data preceding the day it predicts, and it covers the whole sub-hourly era."
          : source === "published"
            ? "Measured against the P50 series actually published day-ahead."
            : "",
      applied: true,
    };
  }, [isMeasured, current, factor]);

  // The applied measurement was taken on a different battery - typically a
  // provenance that travelled with a copied project whose battery changed.
  const otherBattery = appliedCard
    ? foresightBatteryMismatch(current.bessDescription, projectBatteryKw, projectBatteryKwh)
    : "";

  const shown = appliedCard
    // Nothing applied: still show an unusable server measurement, because it
    // carries the reason the window or the forecast series is not good enough,
    // which is what the operator needs to see.
    || ((projectMeasurement && !projectMeasurement.usable) ? projectMeasurement : null)
    || (basis === "reference" ? referenceMeasurement : null);

  // The server holds a measurement that is NOT the one this project applies.
  const serverLatest = projectMeasurement || referenceMeasurement;
  const serverDiffers = Boolean(
    appliedCard && serverLatest
      && Math.abs(Number(serverLatest.factor) - Number(factor)) > 5e-4
  );

  // The select must name what is APPLIED. When that is not the platform's own
  // measurement for the scope (copied with the project, another battery, or an
  // earlier one kept frozen), it gets its own entry instead of borrowing the
  // label of the platform's latest - which may be a different, unusable figure.
  const frozenLabel = frozenForesightOption({
    factor,
    provenance: current,
    serverMeasurement:
      basis === "project" ? projectMeasurement : basis === "reference" ? referenceMeasurement : null,
    otherBattery,
  });
  const selectValue = frozenLabel ? "applied" : basis;

  return (
    <Box sx={{ mt: 1 }}>
      <LabeledField
        select
        label="Dispatch realism (capture ratio) basis"
        value={selectValue}
        onChange={handleBasisChange}
        fullWidth
        helperText={
          loading
            ? "Loading the measurements this platform has taken…"
            : "The dispatch is optimised with perfect price foresight; real day-ahead bidding on forecasts captures less. This is how much."
        }
      >
        {frozenLabel ? <MenuItem value="applied">{frozenLabel}</MenuItem> : null}
        <MenuItem
          value="project"
          disabled={!projectMeasurement || !projectMeasurement.usable}
        >
          {projectMeasurement
            ? `Measured — this project's battery (${projectMeasurement.factor.toFixed(3)}${
                projectMeasurement.usable
                  ? ""
                  : ` — not usable, ${projectMeasurement.provenance.daysEvaluated} days`
              })`
            : "Measured — this project's battery (not measured yet)"}
        </MenuItem>
        <MenuItem
          value="reference"
          disabled={!referenceMeasurement || !referenceMeasurement.usable}
        >
          {referenceMeasurement
            ? `Measured — ${market} market reference (${referenceMeasurement.factor.toFixed(3)}${
                referenceMeasurement.usable ? "" : " — not usable"
              })`
            : `Measured — ${market || "market"} reference (none available)`}
        </MenuItem>
        <MenuItem value="manual">Manual assumption</MenuItem>
        <MenuItem value="none">No haircut (1.00)</MenuItem>
      </LabeledField>

      {shown ? (
        <Alert
          severity={!shown.usable ? "error" : issue ? "warning" : "success"}
          sx={{ mt: 1 }}
        >
          {!shown.usable ? (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {`Not usable as a basis — ${shown.unusable_reason}`}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {shown.applied ? "Applied: " : ""}
            {shown.factor.toFixed(4)} · {shown.provenance.market} ·{" "}
            {shown.provenance.daysEvaluated} delivery days
          </Typography>
          <Typography variant="caption" component="div">
            {[
              `${shown.provenance.periodStart} to ${shown.provenance.periodEnd}`,
              shown.provenance.measuredOn ? `frozen ${shown.provenance.measuredOn}` : "",
              shown.provenance.socMode ? `SoC ${shown.provenance.socMode}` : "",
              shown.provenance.forecastSource || "",
            ].filter(Boolean).join(" · ")}
          </Typography>
          <Typography variant="caption" component="div">
            {shown.forecast_source_note}
          </Typography>
          <Typography variant="caption" component="div">
            {`Battery measured: ${shown.bess_description}${
              shown.scope === "reference"
                ? " — a market reference, not this project's battery. The report says so."
                : ""
            }`}
          </Typography>
          {otherBattery ? (
            <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
              {`Measured on a different battery than this project's (${otherBattery}). The ratio is indicative for this project; measure this project's battery before giving the study to a lender.`}
            </Typography>
          ) : null}
          {!shown.window_is_annual ? (
            <Typography variant="caption" component="div">
              The window is shorter than a full year, so it does not yet carry a complete
              seasonal cycle of day-ahead spreads. The report discloses this.
            </Typography>
          ) : null}
          {(shown.warnings || []).map((warning) => (
            <Typography key={warning} variant="caption" component="div">
              ⚠ {warning}
            </Typography>
          ))}
          {issue ? (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {issue}
            </Typography>
          ) : null}
          {serverDiffers ? (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {`This platform's latest measurement for this project is ${serverLatest.factor.toFixed(4)} over ${serverLatest.provenance.daysEvaluated} days${
                serverLatest.usable
                  ? " — select it above to adopt it."
                  : ` — not usable (${serverLatest.unusable_reason})`
              } The figure applied above stays frozen until you change it.`}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {basis === "manual" ? (
        <LabeledField
          label="Dispatch-realism factor (manual)"
          type="number"
          value={factor ?? 1}
          onChange={(event) => {
            setManualChosen(true);
            onFactorChange(event.target.value);
          }}
          placeholder="e.g. 0.90"
          helperText="A typed value is reported as an assumption, never as a measurement. Published forecast-vs-hindsight studies sit at 0.85-0.95; state your basis. Under 11A it applies only to the battery-at-DAM share."
          fullWidth
          sx={{ mt: 1 }}
        />
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={measuring || !projectId || !market}
          onClick={runMeasurement}
        >
          {measuring ? "Measuring… (a few minutes)" : "Measure this project's battery"}
        </Button>
        <Button size="small" onClick={() => setAdvancedOpen((open) => !open)}>
          {advancedOpen ? "Hide advanced" : "Advanced"}
        </Button>
      </Stack>
      {!projectId ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Save the project first to measure its own battery.
        </Typography>
      ) : null}
      {measuring ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          One dispatch optimisation per delivery day, twice — once on the day-ahead forecast,
          once on the prices the day actually settled at. You can leave this page.
        </Typography>
      ) : null}
      {measureError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {measureError}
        </Alert>
      ) : null}
      {loadError && !shown ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No measurement is available from the platform yet ({loadError}). The manual basis is
          unaffected.
        </Alert>
      ) : null}

      <Collapse in={advancedOpen}>
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={8}
            label="Paste a measurement (offline / no database)"
            value={pasted}
            onChange={(event) => setPasted(event.target.value)}
            placeholder="python scripts/capture_backtest.py --market GR --emit-config"
            error={Boolean(parsedPaste.error)}
            helperText={
              parsedPaste.error
                ? parsedPaste.error
                : parsedPaste.ok
                  ? `Ready: ${parsedPaste.summary}${parsedPaste.warning ? ` — note: ${parsedPaste.warning}` : ""}`
                  : "For deployments with no database access. Accepts the --emit-config block or a whole capture_backtest_<MARKET>.json artifact; the factor comes from the measurement, so the two cannot disagree."
            }
            sx={{ ...economicsFieldSx }}
            InputLabelProps={{ shrink: true, sx: economicsInputLabelSx }}
          />
          <Button
            size="small"
            variant="contained"
            sx={{ mt: 1 }}
            disabled={!parsedPaste.ok}
            onClick={() => {
              onApply(parsedPaste.factor, parsedPaste.provenance);
              setPasted("");
            }}
          >
            Apply pasted measurement
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}


const investmentHints = {
  moduleEurPerWdc: {
    placeholder: "e.g. 0.10",
    helperText: "Base installation cost of PV modules including mounting structures, DC cabling, and electrical balance of system.",
  },
  inverterEurPerWdc: {
    placeholder: "e.g. 0.05",
    helperText: "Base cost for inverter installation and integration, including wiring and connection to the PV system. Multiplied by the inverters' rated AC power.",
  },
  batteryEurPerWhdc: {
    placeholder: "e.g. 0.08",
    helperText: "Base cost for battery system integration, including containerization, HVAC, PCS integration, and electrical balance of system.",
  },
  gridInterconnectionEurPerKwp: {
    placeholder: "e.g. 80.0",
    helperText: "Grid connection cost per kWp.",
  },
  transformerEurPerKva: {
    placeholder: "e.g. 100.0",
    helperText:
      "Transformer procurement cost per kVA — multiplied by the transformer rating: the rated kVA set on the Grid Connection page, or the recommended rating when none is set.",
  },
  designEngineeringEurPerKwp: {
    placeholder: "e.g. 25.0",
    helperText: "Design and engineering services per kWp, including the office's fees. Included in study CAPEX and all commercial offers; re-run the study after changing this value.",
  },
  laborPermitsPctSubtotal: {
    placeholder: "e.g. 5.0",
    helperText: "Enter as a % of total installation cost.",
  },
  moduleRecurringPctYear: {
    placeholder: "e.g. 1.0",
    helperText: "Annual module O&M (% of module CAPEX).",
  },
  inverterRecurringPctYear: {
    placeholder: "e.g. 1.5",
    helperText: "Annual inverter O&M (% of inverter CAPEX).",
  },
  batteryRecurringPctYear: {
    placeholder: "e.g. 2.0",
    helperText: "Annual battery O&M (% of battery CAPEX).",
  },
  insuranceRatePctYear: {
    placeholder: "e.g. 0.5",
    helperText: "Annual insurance rate (% of project's CAPEX).",
  },
  landLeasingEurYear: {
    placeholder: "e.g. 15000",
    helperText: "Land lease per year (set to zero if owned).",
  },
  mountingStructuresEurPerKwp: {
    placeholder: "e.g. 70.0",
    helperText: "Mounting structure cost per kWp.",
  },
  commissioningTestsEurPerKwp: {
    placeholder: "e.g. 8.0",
    helperText: "Commissioning and testing cost per kWp.",
  },
  cablingConnectionsEurPerKwp: {
    placeholder: "e.g. 20",
    helperText: "Cabling and connection works per kWp.",
  },
  electricalProtectionEurSite: {
    placeholder: "e.g. 12000",
    helperText: "Protection & distribution cost per site.",
  },
  monitoringMeteringEurSite: {
    placeholder: "e.g. 8000",
    helperText: "Monitoring and metering systems cost per site.",
  },
  contingencyPctSubtotal: {
    placeholder: "e.g. 5.0",
    helperText: "Contingency entered as % of total installation cost.",
  },
};


const financialHints = {
  inflationRatePctYear: {
    placeholder: "e.g. 2.0",
    helperText: "Annual inflation assumption entered as a whole percentage.",
  },
  realDiscountRate: {
    placeholder: "e.g. 5.0",
    helperText: "Real discount rate used by the detailed model.",
  },
  incomeTax: {
    placeholder: "e.g. 22.0",
    helperText: "Income tax assumption used by the detailed model.",
  },
  salvageValue: {
    placeholder: "e.g. 5",
    helperText: "Residual asset value at project end (% of project's CAPEX).",
  },
  capexGrantPct: {
    placeholder: "e.g. 2.0",
  },
  generationIncentiveEurPerKwh: {
    placeholder: "e.g. 0.05",
    helperText: "Generation-based support in €/kWh (taxed as revenue).",
  },
  debtPercent: {
    placeholder: "e.g. 70.0",
    helperText: "Debt share of total CAPEX.",
  },
  debtSizingMethod: {
    helperText:
      "Defines whether the model uses the entered debt percentage directly or sizes debt from the target DSCR. Bankability reports should clearly state the selected method.",
  },
  tenorYears: {
    placeholder: "e.g. 10",
    helperText: "Loan tenor in full years.",
  },
  annualInterestRatePct: {
    placeholder: "e.g. 5.0",
    helperText: "Nominal annual interest rate.",
  },
  moratoriumYears: {
    placeholder: "e.g. 0",
    helperText: "Years before scheduled principal repayment begins.",
  },
  debtClosingCostsEur: {
    placeholder: "e.g. 0.0",
    helperText: "One-time debt closing costs.",
  },
  upFrontFeePct: {
    placeholder: "e.g. 1.0",
    helperText: "Bank up-front fee as a whole percentage of debt.",
  },
  loanStructure: {
    helperText: "Select the debt repayment structure used in the financing model.",
  },
  targetDSCR: {
    placeholder: "e.g. 1.30",
    helperText: "Target debt service coverage ratio used for sculpted debt.",
  },
  minimumDscrCovenant: {
    placeholder: "e.g. 1.25",
    helperText: "Minimum DSCR covenant used for bankability pass/fail reporting in the exported PDF.",
  },
  debtServiceFrequency: {
    helperText:
      "Frequency of scheduled principal and interest payments. Annual is active in V1; subannual options are shown for future backend support.",
  },
  dsraMonths: {
    placeholder: "e.g. 6",
    helperText:
      "Debt service reserve account sized as months of scheduled debt service. If DSRA funding is not modelled, it is disclosed in the report but not included as a cash-flow adjustment.",
  },
  dsraFundingMethod: {
    helperText: "Defines whether DSRA funding is ignored, funded upfront, or funded from project cash flow.",
  },
  moratoriumInterestTreatment: {
    helperText: "Defines how interest is treated during principal grace period.",
  },
  interestType: {
    helperText: "Fixed is the default. Floating rate inputs are disclosure-only until backend support is implemented.",
  },
  baseRatePct: {
    placeholder: "e.g. 3.0",
    helperText: "Floating-rate base rate disclosure.",
  },
  marginPct: {
    placeholder: "e.g. 2.0",
    helperText: "Floating-rate margin disclosure.",
  },
  bulletMaturityYear: {
    placeholder: "e.g. 10",
    helperText: "Year in which principal is repaid for bullet debt. Usually equals tenor.",
  },
  balloonRepaymentPct: {
    placeholder: "e.g. 100",
    helperText: "Percentage of principal repaid as balloon at maturity.",
  },
  sculptingMode: {
    helperText:
      "Sculpted debt can either shape repayments around a fixed debt amount or size the debt amount from target DSCR. The selected mode is shown in the report.",
  },
  graceInterestCapitalized: {
    helperText: "If enabled, interest during moratorium is capitalized into the loan balance.",
  },
  facilityType: {
    helperText: "Debt facility type for lender disclosure.",
  },
  facilityStatus: {
    helperText: "Current status of the debt facility.",
  },
  securityPackageStatus: {
    helperText: "Security package type for lender disclosure.",
  },
  constructionDrawdownMode: {
    helperText:
      "How construction financing is drawn down. Not modelled in V1 cash flow unless explicitly supported by the backend.",
  },
  constructionMonths: {
    placeholder: "e.g. 12",
    helperText: "Construction period in months.",
  },
  interestDuringConstructionTreatment: {
    helperText:
      "How interest accrued during construction is treated. Disclosure-only unless modelled by the backend.",
  },
  commitmentFeePct: {
    placeholder: "e.g. 0.5",
    helperText: "Commitment fee on undrawn facility (% per annum). Disclosure-only.",
  },
  arrangementFeePct: {
    placeholder: "e.g. 1.0",
    helperText: "Arrangement fee on total facility (% of debt). Disclosure-only.",
  },
  llcrCovenant: {
    placeholder: "e.g. 1.30",
    helperText: "Loan life coverage ratio covenant (LLCR). Must be greater than 0 if provided.",
  },
  plcrCovenant: {
    placeholder: "e.g. 1.25",
    helperText: "Project life coverage ratio covenant (PLCR). Must be greater than 0 if provided.",
  },
  cashSweepEnabled: {
    helperText: "Enable cash sweep — mandatory excess cash flow applied to debt prepayment.",
  },
  cashSweepPct: {
    placeholder: "e.g. 50",
    helperText: "Percentage of distributable cash flow swept to debt prepayment.",
  },
  refinancingAssumption: {
    helperText:
      "Whether the model assumes refinancing at mini-perm maturity or after the contracted period.",
  },
  refinancingYear: {
    placeholder: "e.g. 7",
    helperText: "Year in which refinancing is assumed to occur. Must be no later than tenor.",
  },
  customDebtScheduleEnabled: {
    helperText:
      "Custom debt schedules are planned for a future version unless backend support exists.",
  },
};

const MERCHANT_REVENUE_MULTIPLIER_KEYS = ["multiplier", "value"];
const FORWARD_LEVEL_KEYS = ["levelEurMwh", "priceEurMwh", "value"];
const MERCHANT_REVENUE_VALUE_KEYS = [
  "merchantRevenueEurYear",
  "merchantRevenueEur",
  "revenueEurYear",
  "revenueEur",
  "value",
];

function parseFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function getCurveEntryValue(entry, valueKeys) {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    const key = valueKeys.find((candidate) => entry[candidate] !== undefined);
    return key ? entry[key] : "";
  }
  return entry ?? "";
}

function getCurveValue(curve, index, valueKeys) {
  if (!Array.isArray(curve)) return "";
  return getCurveEntryValue(curve[index], valueKeys);
}

function trimTrailingBlankCurveValues(values) {
  const next = [...values];
  while (next.length) {
    const last = next[next.length - 1];
    if (last !== "" && last != null) break;
    next.pop();
  }
  return next;
}

function setCurveValue(curve, index, rawValue, yearCount, valueKeys) {
  const next = Array.from({ length: yearCount }, (_, rowIndex) =>
    getCurveValue(curve, rowIndex, valueKeys)
  );
  next[index] = rawValue;
  return trimTrailingBlankCurveValues(next);
}

function getProjectAnalysisYears(config) {
  const candidates = [
    config?.feasibility?.settlementPeriod,
    config?.feasibility?.analysisYears,
    config?.feasibility?.analysis_years,
    config?.analysisYears,
    config?.analysis_years,
    config?.projectTermYears,
    config?.project_term_years,
    config?.tollingContractYears,
  ];
  const parsed = candidates
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value) && value > 0);
  return parsed ? Math.max(1, Math.round(parsed)) : 20;
}

function hasMissingCurveValues(curve, yearCount, valueKeys) {
  for (let index = 0; index < yearCount; index += 1) {
    const value = getCurveValue(curve, index, valueKeys);
    if (value === "" || value == null) return true;
  }
  return false;
}

function hasNegativeCurveValues(curve, yearCount, valueKeys) {
  for (let index = 0; index < yearCount; index += 1) {
    const parsed = parseFiniteNumber(getCurveValue(curve, index, valueKeys));
    if (parsed != null && parsed < 0) return true;
  }
  return false;
}

function MerchantRevenueCurveTable({
  label,
  valueLabel,
  values,
  valueKeys,
  yearCount,
  onValueChange,
  allowNegative = false,
  startCalendarYear = null,
  blankPlaceholder = null,
}) {
  // A leading blank is the simulation-year level; a later blank carries the
  // previous year, so the placeholder names only the leading ones.
  let firstFilled = 0;
  while (firstFilled < yearCount && parseFiniteNumber(getCurveValue(values, firstFilled, valueKeys)) == null) firstFilled += 1;
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "72px minmax(0, 1fr)", sm: "90px minmax(0, 1fr)" },
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          Year
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          {valueLabel}
        </Typography>
      </Box>
      {Array.from({ length: yearCount }, (_, index) => {
        const year = index + 1;
        const currentValue = getCurveValue(values, index, valueKeys);
        const parsed = parseFiniteNumber(currentValue);
        const isInvalid = !allowNegative && parsed != null && parsed < 0;
        return (
          <Box
            key={year}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "72px minmax(0, 1fr)", sm: "90px minmax(0, 1fr)" },
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {startCalendarYear ? `${year} (${startCalendarYear + index})` : year}
            </Typography>
            <TextField
              size="small"
              value={currentValue}
              onChange={(event) => onValueChange(index, event.target.value)}
              error={isInvalid}
              helperText={isInvalid ? "Must be non-negative." : ""}
              inputMode="decimal"
              placeholder={(blankPlaceholder && index < firstFilled) ? blankPlaceholder : (label === "multiplier" ? "1.00" : "0")}
              inputProps={{
                "aria-label": `${valueLabel} year ${year}`,
              }}
              sx={economicsFieldSx}
            />
          </Box>
        );
      })}
    </Box>
  );
}
// The fields of the «Οικονομικές Παράμετροι» tab. A deep link to one of them
// ("/feasibility/economics#field-econ-discount", see projectFieldLinks.js and
// navigation/useFieldFocus.js) opens the page on that tab, so the field exists
// and can be focused; any other link opens the first tab, as before.
export const FINANCIAL_TAB_FIELD_IDS = [
  "field-econ-inflation",
  "field-econ-discount",
  "field-econ-debt-percent",
  "field-econ-income-tax",
  "field-econ-salvage",
  "field-econ-grant",
  "field-econ-incentive",
];

export function initialEconomicsTab(hash) {
  const id = String(hash || "").replace(/^#/, "");
  return FINANCIAL_TAB_FIELD_IDS.includes(id) ? "financial" : "investment";
}

function TabPanel({ value, current, children }) {
  if (value !== current) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}


export default function FeasibilityEconomics() {
  const { isGreek } = useLanguage();
  const history = useHistory();
  // The project id is what makes a per-project measurement possible; an unsaved
  // project can still use the market reference or a manual assumption.
  const { currentProject } = useFile();
  const cfg = useMemo(() => history.present ?? DEFAULT_CONFIG, [history.present]);

  const mergedCfg = useMemo(() => {
    const base = DEFAULT_CONFIG;
    const next = { ...base, ...cfg };

    next.feasibility = {
      ...base.feasibility,
      ...(cfg.feasibility || {}),
    };

    next.feasibility.technical = {
      ...base.feasibility.technical,
      ...(cfg.feasibility?.technical || {}),
    };

    next.feasibility.economics = {
      ...base.feasibility.economics,
      ...(cfg.feasibility?.economics || {}),
    };

    next.feasibility.economics.investmentCosts = {
      ...base.feasibility.economics.investmentCosts,
      ...(cfg.feasibility?.economics?.investmentCosts || {}),
    };

    next.feasibility.economics.equipmentCostOverride = {
      ...base.feasibility.economics.equipmentCostOverride,
      ...(cfg.feasibility?.economics?.equipmentCostOverride || {}),
    };

    next.feasibility.economics.financialParams = {
      ...base.feasibility.economics.financialParams,
      ...(cfg.feasibility?.economics?.financialParams || {}),
    };

    next.feasibility.economics.projectDebt = {
      ...DEFAULT_PROJECT_DEBT,
      ...(cfg.feasibility?.economics?.projectDebt || {}),
    };

    next.feasibility.economics.ppa = {
      ...base.feasibility.economics.ppa,
      ...(cfg.feasibility?.economics?.ppa || {}),
    };

    next.merchantRevenueProjection = normalizeMerchantRevenueProjection(next);

    return normalizeProjectDebtConfig(next);
  }, [cfg]);

  const feasibility = mergedCfg.feasibility;
  const economics = feasibility.economics;
  const investmentCosts = economics.investmentCosts;
  const financialParams = economics.financialParams;
  const projectDebt = economics.projectDebt;
  /* Availability is entered on two different pages and multiplies when both
     are set. Caught here, at the field that is usually the wrong one, rather
     than left to the report's after-the-fact disclosure. */
  const batteryPerformanceCfg = mergedCfg.batteryPerformance;
  const bessAvailabilityPctInput = financialParams.bessAvailabilityPct;
  const availabilityIssue = useMemo(
    () => availabilityModellingIssue({
      batteryPerformance: batteryPerformanceCfg,
      bessAvailabilityPct: bessAvailabilityPctInput,
    }),
    [batteryPerformanceCfg, bessAvailabilityPctInput]
  );
  const systemTypeLabel = String(mergedCfg.systemType || "Hybrid");
  const normalizedSystemType = systemTypeLabel.trim().toLowerCase();
  const isPvOnlyAsset = normalizedSystemType === "standalone pv";
  const isStandaloneBess =
    normalizedSystemType === "standalone bess" ||
    (normalizedSystemType.includes("standalone") && normalizedSystemType.includes("bess"));
  const [activeTab, setActiveTab] = useState(() => initialEconomicsTab(typeof window === "undefined" ? "" : window.location.hash));
  const [revenueCurveExpanded, setRevenueCurveExpanded] = useState(false);
  const [advancedLenderExpanded, setAdvancedLenderExpanded] = useState(false);

  // ---------------------------------------------------------------------
  // Equipment cost from the CATALOG (read-only disclosure). The backend
  // books the hardware as: modules x unit Price + inverters x unit Price +
  // battery Price x count (the "hybrid upfront cost" CAPEX term). It does
  // NOT come from the EUR/Wdc fields below - those add installation/EPC on
  // top. This block mirrors the backend arithmetic so the CAPEX equipment
  // basis is visible before the run. Counts come from the saved PV
  // electrical design; without one, only the unit price can be shown
  // (auto-design decides the counts at run time).
  // ---------------------------------------------------------------------
  const equipmentModuleName = isStandaloneBess ? "" : String(mergedCfg.read_module || "");
  const equipmentInverterName = isStandaloneBess ? "" : String(mergedCfg.read_inverter || "");
  const pvIsExistingAsset =
    !isStandaloneBess && String(mergedCfg.pvAssetStatus || "new") === "existing";
  const activeBatteryName = isPvOnlyAsset ? "" : String(
    mergedCfg.bess?.activeName ||
      (Array.isArray(mergedCfg.bess?.selected) ? mergedCfg.bess.selected[0]?.name : "") ||
      ""
  );
  const activeBatteryCount = Math.max(
    1,
    Number(
      (Array.isArray(mergedCfg.bess?.selected)
        ? mergedCfg.bess.selected.find((b) => b?.name === activeBatteryName)
        : null)?.count
    ) || 1
  );
  const activeBatteryEntry = Array.isArray(mergedCfg.bess?.selected)
    ? mergedCfg.bess.selected.find((b) => b?.name === activeBatteryName)
    : null;
  // The whole installed battery, as the engine sees it (unit x count).
  const activeBatteryPowerKw =
    (Number(activeBatteryEntry?.p_max_d) || 0) * activeBatteryCount;
  const activeBatteryEnergyKwh =
    (Number(activeBatteryEntry?.energy) || 0) * activeBatteryCount;
  const otherSelectedBatteryCount = Array.isArray(mergedCfg.bess?.selected)
    ? Math.max(0, mergedCfg.bess.selected.length - 1)
    : 0;
  const designCounts = useMemo(() => {
    const systemDesign = mergedCfg.systemDesign;
    if (!systemDesign || typeof systemDesign !== "object") {
      return { modules: 0, inverters: 0 };
    }
    const keys = Object.keys(systemDesign);
    let modules = 0;
    keys.forEach((key) => {
      const mppts = Array.isArray(systemDesign[key]) ? systemDesign[key] : [];
      mppts.forEach((mppt) => {
        const inputs = Array.isArray(mppt?.inputs) ? mppt.inputs : [];
        inputs.forEach((inp) => {
          const mps = Number(inp?.modules_per_string);
          if (Number.isFinite(mps) && mps > 0) modules += mps;
        });
      });
    });
    return { modules, inverters: keys.length };
  }, [mergedCfg.systemDesign]);

  const [equipmentPrices, setEquipmentPrices] = useState({
    modulePrice: null,
    inverterPrice: null,
    batteryPrice: null,
    modulePowerW: null,
  });

  useEffect(() => {
    let cancelled = false;
    async function loadEquipmentPrices() {
      const next = { modulePrice: null, inverterPrice: null, batteryPrice: null, modulePowerW: null };
      if (equipmentModuleName) {
        try {
          const det = await getModuleDetails(equipmentModuleName);
          const raw = Number(det?.characteristics?.Price);
          next.modulePrice = Number.isFinite(raw) ? raw : null;
          // Module rating, for the simplified costing's kWp preview.
          const stc = Number(det?.characteristics?.STC);
          next.modulePowerW = Number.isFinite(stc) && stc > 0 ? stc : null;
        } catch {
          /* row shows an em-dash */
        }
      }
      if (equipmentInverterName) {
        try {
          const det = await getInverterDetails(equipmentInverterName);
          const raw = Number(det?.characteristics?.Price);
          next.inverterPrice = Number.isFinite(raw) ? raw : null;
        } catch {
          /* row shows an em-dash */
        }
      }
      if (activeBatteryName) {
        try {
          const det = await getBatteryDetails(activeBatteryName);
          const raw = Number(det?.characteristics?.Price);
          next.batteryPrice = Number.isFinite(raw) ? raw : null;
        } catch {
          /* row shows an em-dash */
        }
      }
      if (!cancelled) setEquipmentPrices(next);
    }
    loadEquipmentPrices();
    return () => {
      cancelled = true;
    };
  }, [equipmentModuleName, equipmentInverterName, activeBatteryName]);

  const formatEur = (v) =>
    v == null ? "—" : `${Math.round(v).toLocaleString("en-US")} ${EURO}`;
  // Costing method: "detailed" unless the project chose "simplified".
  const costingMode = costingModeOf(investmentCosts);
  // Installed DC kWp from the saved design (auto-design decides it at run
  // time otherwise, and the preview then says so).
  const installedKwp =
    !isStandaloneBess && designCounts.modules > 0 && equipmentPrices.modulePowerW
      ? (designCounts.modules * equipmentPrices.modulePowerW) / 1000
      : null;
  const moduleEquipmentCost =
    equipmentPrices.modulePrice != null && designCounts.modules > 0
      ? equipmentPrices.modulePrice * designCounts.modules
      : null;
  const inverterEquipmentCost =
    equipmentPrices.inverterPrice != null && designCounts.inverters > 0
      ? equipmentPrices.inverterPrice * designCounts.inverters
      : null;
  const batteryEquipmentCost =
    equipmentPrices.batteryPrice != null
      ? equipmentPrices.batteryPrice * activeBatteryCount
      : null;

  // ---------------------------------------------------------------------
  // Overrides. The catalog figures above are what the backend books by
  // default and what a reviewer can check; an override replaces one of them
  // for the cases the catalog cannot express -- a negotiated price, a quoted
  // package, hardware bought before the study existed.
  //
  // It replaces that value EVERYWHERE, not just in the CAPEX line: the
  // percentage O&M and insurance rates are charged on the hardware value, so
  // a study where the modules cost 150k in CAPEX and 190k for insurance would
  // be two studies wearing one number. The backend reads the same override for
  // both (pv_hardware_value / inverter_hardware_value).
  //
  // Empty string, not zero, is "no override": zero is a legitimate figure --
  // donated or sunk hardware -- and must be distinguishable from blank.
  // ---------------------------------------------------------------------
  const equipmentOverride =
    economics.equipmentCostOverride || DEFAULT_EQUIPMENT_COST_OVERRIDE;
  const [equipmentEditing, setEquipmentEditing] = useState(false);
  const [equipmentOverrideCleared, setEquipmentOverrideCleared] = useState([]);

  const moduleCostOverride = parseOverrideEur(equipmentOverride.modules);
  const inverterCostOverride = parseOverrideEur(equipmentOverride.inverters);
  const batteryCostOverride = isPvOnlyAsset ? null : parseOverrideEur(equipmentOverride.battery);
  const anyEquipmentOverride =
    moduleCostOverride != null ||
    inverterCostOverride != null ||
    batteryCostOverride != null;

  const effectiveModuleCost = moduleCostOverride ?? moduleEquipmentCost;
  const effectiveInverterCost = inverterCostOverride ?? inverterEquipmentCost;
  const effectiveBatteryCost = batteryCostOverride ?? batteryEquipmentCost;

  const equipmentCapexTotal = (() => {
    const parts = [];
    if (!pvIsExistingAsset) {
      if (effectiveModuleCost != null) parts.push(effectiveModuleCost);
      if (effectiveInverterCost != null) parts.push(effectiveInverterCost);
    }
    if (!isPvOnlyAsset && effectiveBatteryCost != null) parts.push(effectiveBatteryCost);
    return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
  })();

  const merchantRevenueProjection =
    mergedCfg.merchantRevenueProjection ?? DEFAULT_MERCHANT_REVENUE_PROJECTION;
  const merchantProjectionMode =
    merchantRevenueProjection.mode || DEFAULT_MERCHANT_REVENUE_PROJECTION.mode;
  const selectedMerchantProjectionMode =
    MERCHANT_REVENUE_PROJECTION_MODES.find((mode) => mode.value === merchantProjectionMode) ||
    MERCHANT_REVENUE_PROJECTION_MODES[0];
  const projectionYearCount = getProjectAnalysisYears(mergedCfg);
  const firstProjectionCalendarYear = operatingStartYear(mergedCfg);

  const inflationPct = Number(financialParams.inflationRatePctYear || 0);
  const realDiscountPct = Number(financialParams.realDiscountRate || 0);

  const hasInflationInput =
    financialParams.inflationRatePctYear !== "" &&
    financialParams.inflationRatePctYear != null;

  const hasRealDiscountInput =
    financialParams.realDiscountRate !== "" &&
    financialParams.realDiscountRate != null;

  const calculatedNominalDiscountPct =
    hasInflationInput && hasRealDiscountInput
      ? (((1 + realDiscountPct / 100) * (1 + inflationPct / 100) - 1) * 100)
      : null;
  // With no real rate the nominal one is not calculated, but the run still
  // reads the stored nominalDiscountRatePctYear (read_config.nomDiscountRate):
  // show that figure rather than an empty field. Display only.
  const storedNominalDiscountPct = (() => {
    const raw = financialParams.nominalDiscountRatePctYear;
    if (raw === "" || raw == null) return null;
    const value = Number(String(raw).replace(",", "."));
    return Number.isFinite(value) ? value : null;
  })();
  const merchantAnnualEscalationPct = parseFiniteNumber(
    merchantRevenueProjection.annualEscalationPct
  );
  const hasMerchantAnnualEscalationWarning =
    merchantProjectionMode === "custom_escalation" &&
    merchantAnnualEscalationPct != null &&
    (merchantAnnualEscalationPct < -10 || merchantAnnualEscalationPct > 10);
  const hasMissingMultiplierYears =
    merchantProjectionMode === "custom_curve_multipliers" &&
    hasMissingCurveValues(
      merchantRevenueProjection.customAnnualMultipliers,
      projectionYearCount,
      MERCHANT_REVENUE_MULTIPLIER_KEYS
    );
  const hasMissingRevenueYears =
    merchantProjectionMode === "custom_curve_revenues" &&
    hasMissingCurveValues(
      merchantRevenueProjection.customAnnualRevenues,
      projectionYearCount,
      MERCHANT_REVENUE_VALUE_KEYS
    );
  const hasNegativeMultiplierYears =
    merchantProjectionMode === "custom_curve_multipliers" &&
    hasNegativeCurveValues(
      merchantRevenueProjection.customAnnualMultipliers,
      projectionYearCount,
      MERCHANT_REVENUE_MULTIPLIER_KEYS
    );
  const hasNegativeRevenueYears =
    merchantProjectionMode === "custom_curve_revenues" &&
    hasNegativeCurveValues(
      merchantRevenueProjection.customAnnualRevenues,
      projectionYearCount,
      MERCHANT_REVENUE_VALUE_KEYS
    );


  const setFeasibility = useCallback(
    (path, value) => {
      history.commit(setPathInObject(mergedCfg, `feasibility.${path}`, value));
    },
    [mergedCfg, history]
  );

  const updateInvestment = useCallback(
    (key) => (e) => {
      if (isStandaloneBess && STANDALONE_BESS_ZERO_INVESTMENT_KEY_SET.has(key)) return;
      const raw = e.target.value.replace(",", ".");
      if (!/^\d*\.?\d*$/.test(raw)) return;
      setFeasibility(`economics.investmentCosts.${key}`, raw);
    },
    [isStandaloneBess, setFeasibility]
  );

  const updateFinancial = useCallback(
    (key) => (e) => {
      const raw = e.target.value;

      if (key === "loanStructure") {
        setFeasibility(`economics.financialParams.${key}`, raw);
        return;
      }

      const normalized = raw.replace(",", ".");
      if (!/^\d*\.?\d*$/.test(normalized)) return;
      setFeasibility(`economics.financialParams.${key}`, normalized);
    },
    [setFeasibility]
  );

  const updateProjectDebt = useCallback(
    (key) => (e) => {
      const raw = e.target.value;
      const textKeys = new Set([
        "loanStructure",
        "debtSizingMethod",
        "debtServiceFrequency",
        "dsraFundingMethod",
        "moratoriumInterestTreatment",
        "interestType",
        "sculptingMode",
        "facilityType",
        "facilityStatus",
        "constructionDrawdownMode",
        "interestDuringConstructionTreatment",
        "securityPackageStatus",
        "refinancingAssumption",
      ]);
      const normalized = textKeys.has(key) ? raw : raw.replace(",", ".");
      if (!textKeys.has(key) && !/^\d*\.?\d*$/.test(normalized)) return;

      const nextDebt = {
        ...projectDebt,
        [key]: normalized,
      };
      if (key === "loanStructure" && normalized === "bullet" && !nextDebt.bulletMaturityYear) {
        nextDebt.bulletMaturityYear = nextDebt.tenorYears || financialParams.tenorYears || "";
      }
      if (key === "tenorYears" && nextDebt.loanStructure === "bullet" && !nextDebt.bulletMaturityYear) {
        nextDebt.bulletMaturityYear = normalized;
      }

      const legacyKeyMap = {
        loanStructure: "loanStructure",
        debtSizingMethod: "debtSizingMethod",
        debtPercent: "debtPercent",
        tenorYears: "tenorYears",
        annualInterestRatePct: "annualInterestRatePct",
        moratoriumYears: "moratoriumYears",
        upfrontFeePct: "upFrontFeePct",
        debtClosingCostsEur: "debtClosingCostsEur",
        targetDscr: "targetDSCR",
        minimumDscrCovenant: "minimumDscrCovenant",
        debtServiceFrequency: "debtServiceFrequency",
        dsraMonths: "dsraMonths",
        dsraFundingMethod: "dsraFundingMethod",
        moratoriumInterestTreatment: "moratoriumInterestTreatment",
        interestType: "interestType",
        baseRatePct: "baseRatePct",
        marginPct: "marginPct",
        bulletMaturityYear: "bulletMaturityYear",
        balloonRepaymentPct: "balloonRepaymentPct",
        sculptingMode: "sculptingMode",
      };

      let nextCfg = setPathInObject(
        mergedCfg,
        "feasibility.economics.projectDebt",
        nextDebt
      );
      const legacyKey = legacyKeyMap[key];
      if (legacyKey) {
        nextCfg = setPathInObject(
          nextCfg,
          `feasibility.economics.financialParams.${legacyKey}`,
          normalized
        );
      }
      if (key === "loanStructure" && normalized === "bullet" && nextDebt.bulletMaturityYear) {
        nextCfg = setPathInObject(
          nextCfg,
          "feasibility.economics.financialParams.bulletMaturityYear",
          nextDebt.bulletMaturityYear
        );
      }
      if (key === "tenorYears" && nextDebt.loanStructure === "bullet" && nextDebt.bulletMaturityYear === normalized) {
        nextCfg = setPathInObject(
          nextCfg,
          "feasibility.economics.financialParams.bulletMaturityYear",
          normalized
        );
      }
      history.commit(nextCfg);
    },
    [financialParams.tenorYears, history, mergedCfg, projectDebt]
  );

  const updateProjectDebtBoolean = useCallback(
    (key) => (event) => {
      const nextDebt = {
        ...projectDebt,
        [key]: event.target.checked,
      };
      history.commit(
        setPathInObject(mergedCfg, "feasibility.economics.projectDebt", nextDebt)
      );
    },
    [history, mergedCfg, projectDebt]
  );

  const setMerchantRevenueProjection = useCallback(
    (patch) => {
      const nextMode = patch.mode ?? merchantRevenueProjection.mode;
      const nextProjection = {
        ...DEFAULT_MERCHANT_REVENUE_PROJECTION,
        ...merchantRevenueProjection,
        ...patch,
        mode: nextMode,
        applyInflationEscalation: nextMode === "inflation_indexed",
      };
      history.commit(setPathInObject(mergedCfg, "merchantRevenueProjection", nextProjection));
    },
    [history, mergedCfg, merchantRevenueProjection]
  );

  const updateMerchantProjectionMode = useCallback(
    (event) => {
      const mode = event.target.value;
      if (mode !== "custom_curve_revenues") {
        setRevenueCurveExpanded(false);
      }
      setMerchantRevenueProjection({ mode });
    },
    [setMerchantRevenueProjection]
  );

  const updateMerchantAnnualEscalation = useCallback(
    (event) => {
      const raw = event.target.value.replace(",", ".");
      if (!/^-?\d*\.?\d*$/.test(raw)) return;
      setMerchantRevenueProjection({ annualEscalationPct: raw });
    },
    [setMerchantRevenueProjection]
  );

  const updateMerchantMultiplierCurveValue = useCallback(
    (index, rawInput) => {
      const raw = rawInput.replace(",", ".");
      if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
      setMerchantRevenueProjection({
        customAnnualMultipliers: setCurveValue(
          merchantRevenueProjection.customAnnualMultipliers,
          index,
          raw,
          projectionYearCount,
          MERCHANT_REVENUE_MULTIPLIER_KEYS
        ),
      });
    },
    [merchantRevenueProjection.customAnnualMultipliers, projectionYearCount, setMerchantRevenueProjection]
  );

  const updateMerchantRevenueCurveValue = useCallback(
    (index, rawInput) => {
      const raw = rawInput.replace(",", ".");
      if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
      setMerchantRevenueProjection({
        customAnnualRevenues: setCurveValue(
          merchantRevenueProjection.customAnnualRevenues,
          index,
          raw,
          projectionYearCount,
          MERCHANT_REVENUE_VALUE_KEYS
        ),
      });
    },
    [merchantRevenueProjection.customAnnualRevenues, projectionYearCount, setMerchantRevenueProjection]
  );

  const forwardCurve = useMemo(
    () => ({
      ...DEFAULT_FORWARD_CURVE,
      ...(merchantRevenueProjection.forwardCurve || {}),
    }),
    [merchantRevenueProjection.forwardCurve]
  );
  const housePathYearMismatch = forwardCurve.housePathStartYear != null &&
    Number(forwardCurve.housePathStartYear) !== firstProjectionCalendarYear;
  const housePathHeldYears = housePathYearsBeforeFirstAnchor(forwardCurve);
  // The level those years are held at: the simulation year's average DAM price,
  // from the same series the study prices against. Shown, not stored.
  const housePathSimYear = Number(mergedCfg.feasibility?.simulationYear) || defaultSimulationYear();
  const housePathZone = normalizeMarketZone(mergedCfg.biddingZone) || "GR";
  const housePathTimeZone = mergedCfg.timeZone || "";
  const [simYearLevel, setSimYearLevel] = useState(null);
  useEffect(() => {
    let alive = true;
    setSimYearLevel(null);
    if (merchantProjectionMode !== "forward_curve_redispatch" || housePathHeldYears <= 0) return undefined;
    fetchDamAnnualMean({ market: housePathZone, year: housePathSimYear, tz: housePathTimeZone || undefined })
      .then((data) => {
        const value = data?.mean_eur_mwh;
        if (alive && value != null && Number.isFinite(Number(value))) {
          setSimYearLevel({ year: housePathSimYear, value: Number(value) });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [merchantProjectionMode, housePathHeldYears, housePathSimYear, housePathZone, housePathTimeZone]);

  const setForwardCurve = useCallback(
    (patch) => {
      setMerchantRevenueProjection({
        forwardCurve: {
          ...DEFAULT_FORWARD_CURVE,
          ...(merchantRevenueProjection.forwardCurve || {}),
          ...patch,
        },
      });
    },
    [merchantRevenueProjection.forwardCurve, setMerchantRevenueProjection]
  );

  const forwardInputMode =
    FORWARD_LEVEL_INPUT_MODES.find((m) => m.value === forwardCurve.levelInputMode) ||
    FORWARD_LEVEL_INPUT_MODES[0];
  const forwardCurveArrayKey =
    forwardInputMode.value === "levels_eur_mwh"
      ? "levelsEurMwh"
      : forwardInputMode.value === "multipliers_vs_base"
        ? "levelMultipliers"
        : "levelDeltasPct";
  const forwardCurveAllowsNegative = forwardCurveArrayKey === "levelDeltasPct";

  const updateForwardLevelCurveValue = useCallback(
    (index, rawInput) => {
      const raw = rawInput.replace(",", ".");
      const pattern = forwardCurveAllowsNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
      if (raw !== "" && !pattern.test(raw)) return;
      setForwardCurve({
        [forwardCurveArrayKey]: setCurveValue(
          forwardCurve[forwardCurveArrayKey],
          index,
          raw,
          projectionYearCount,
          FORWARD_LEVEL_KEYS
        ),
      });
    },
    [forwardCurve, forwardCurveArrayKey, forwardCurveAllowsNegative, projectionYearCount, setForwardCurve]
  );

  useEffect(() => {
    if (!isStandaloneBess) return;

    let changed = false;
    const nextInvestmentCosts = { ...investmentCosts };
    STANDALONE_BESS_ZERO_INVESTMENT_KEYS.forEach((key) => {
      if (Number(nextInvestmentCosts[key]) !== 0) {
        nextInvestmentCosts[key] = 0;
        changed = true;
      }
    });

    if (changed) {
      setFeasibility("economics.investmentCosts", nextInvestmentCosts);
    }
  }, [investmentCosts, isStandaloneBess, setFeasibility]);

  const updateEquipmentOverride = (valueKey, nameKey, currentName) => (event) => {
    const raw = String(event.target.value || "").replace(",", ".");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    setFeasibility("economics.equipmentCostOverride", {
      ...equipmentOverride,
      [valueKey]: raw,
      // Stamped with the model it describes. Cleared along with the value, so
      // an emptied field cannot leave a name behind to be compared against.
      [nameKey]: raw.trim() === "" ? "" : String(currentName || ""),
    });
  };

  const clearEquipmentOverrides = () => {
    setEquipmentOverrideCleared([]);
    setFeasibility(
      "economics.equipmentCostOverride",
      { ...DEFAULT_EQUIPMENT_COST_OVERRIDE }
    );
  };

  // The props that turn one of the three cost rows from a read-only mirror of
  // the catalog into an editable override, and back. Kept in one place so the
  // three rows cannot drift into three behaviours.
  const equipmentCostFieldProps = ({
    valueKey,
    nameKey,
    currentName,
    catalogCost,
    overrideCost,
    baseHelper,
  }) => {
    const catalogText =
      catalogCost == null ? "no catalog figure" : formatEur(catalogCost);
    const gap =
      overrideCost != null && catalogCost != null && catalogCost > 0
        ? ` (${overrideCost >= catalogCost ? "+" : "−"}${(
            (Math.abs(overrideCost - catalogCost) / catalogCost) *
            100
          ).toFixed(1)}% vs catalog)`
        : "";
    if (!equipmentEditing) {
      return {
        value: formatEur(overrideCost ?? catalogCost),
        InputProps: { readOnly: true },
        helperText:
          overrideCost != null
            ? `Overridden — the catalog says ${catalogText}${gap}. Press Edit to change or clear it.`
            : baseHelper,
      };
    }
    return {
      value: String(equipmentOverride[valueKey] ?? ""),
      onChange: updateEquipmentOverride(valueKey, nameKey, currentName),
      placeholder: catalogCost == null ? "" : String(Math.round(catalogCost)),
      helperText:
        overrideCost != null
          ? `Override in force${gap}. Catalog: ${catalogText}. Clear the field to go back to it.`
          : `Leave blank to use the catalog (${catalogText}). A figure here replaces it in CAPEX and in the % O&M / insurance basis.`,
    };
  };

  // An override describes a price for a specific piece of equipment. Swap the
  // equipment and it describes nothing -- so it goes, and says so. Changing
  // only the string design does NOT clear it: the count moved, the hardware
  // did not, and a quoted package price is usually for the package.
  //
  // It lives HERE, below `setFeasibility`, and not beside the rest of the
  // override machinery further up, for one reason: a dependency array is
  // evaluated during render. Naming a `const` declared later in the component
  // throws "Cannot access ... before initialization" and blanks the page --
  // which is exactly what it did. Moving it back up would do it again.
  useEffect(() => {
    const { next, dropped } = pruneStaleOverrides(equipmentOverride, {
      modules: equipmentModuleName,
      inverters: equipmentInverterName,
      battery: activeBatteryName,
    });
    if (dropped.length) {
      setEquipmentOverrideCleared(dropped);
      setFeasibility("economics.equipmentCostOverride", next);
    }
  }, [
    equipmentOverride,
    equipmentModuleName,
    equipmentInverterName,
    activeBatteryName,
    setFeasibility,
  ]);

  const isPvSunkAsset =
    !isStandaloneBess && String(cfg.pvAssetStatus || "new") === "existing";

  const getInvestmentFieldProps = (key) => {
    const lockedZero =
      isStandaloneBess && STANDALONE_BESS_ZERO_INVESTMENT_KEY_SET.has(key);
    const lockedSunkPv = isPvSunkAsset && SUNK_PV_EXCLUDED_INVESTMENT_KEYS.has(key);

    return {
      value: lockedZero || lockedSunkPv ? 0 : investmentCosts[key],
      onChange: updateInvestment(key),
      disabled: lockedZero || lockedSunkPv,
      ...(lockedSunkPv ? { helperText: SUNK_PV_HELPER_TEXT } : {}),
    };
  };

  const tabsWrapperSx = {
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    p: 1,
    background: (t) =>
      t.palette.mode === "dark"
        ? "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01))"
        : t.palette.background.paper,
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? "0 8px 24px rgba(0,0,0,0.18)"
        : "0 8px 20px rgba(0,0,0,0.06)",
  };

  const tabsSx = {
    minHeight: 48,
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: 999,
    },
  };

  const tabSx = {
    textTransform: "none",
    fontWeight: 700,
    minHeight: 48,
    alignItems: "flex-start",
    borderRadius: 2,
  };

  const formatPercent = (value, digits = 2) => {
    if (value == null || !Number.isFinite(value)) return "";
    return value.toFixed(digits).replace(/\.?0+$/, "");
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(1, minmax(0, 1fr))",
      sm: "repeat(2, minmax(0, 1fr))",
      lg: "repeat(3, minmax(0, 1fr))",
    },
    gap: 2,
    width: "100%",
  };

  const grid3Sx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(1, minmax(0, 1fr))",
      sm: "repeat(2, minmax(0, 1fr))",
      lg: "repeat(3, minmax(0, 1fr))",
    },
    gap: 2,
    width: "100%",
  };


  useEffect(() => {
    if (calculatedNominalDiscountPct == null) return;

    const formatted = formatPercent(calculatedNominalDiscountPct, 4);
    if (String(financialParams.nominalDiscountRatePctYear ?? "") !== formatted) {
      setFeasibility("economics.financialParams.nominalDiscountRatePctYear", formatted);
    }
  }, [
    calculatedNominalDiscountPct,
    financialParams.nominalDiscountRatePctYear,
    setFeasibility,
  ]);

  const debtValidation = useMemo(() => {
    const debtPercent = parseFiniteNumber(projectDebt.debtPercent);
    const tenorYears = parseFiniteNumber(projectDebt.tenorYears);
    const annualInterestRate = parseFiniteNumber(projectDebt.annualInterestRatePct);
    const moratoriumYears = parseFiniteNumber(projectDebt.moratoriumYears);
    const targetDscr = parseFiniteNumber(projectDebt.targetDscr);
    const minimumDscr = parseFiniteNumber(projectDebt.minimumDscrCovenant);
    const dsraMonths = parseFiniteNumber(projectDebt.dsraMonths);
    const bulletMaturityYear = parseFiniteNumber(projectDebt.bulletMaturityYear);
    const balloonRepaymentPct = parseFiniteNumber(projectDebt.balloonRepaymentPct);
    const baseRatePct = parseFiniteNumber(projectDebt.baseRatePct);
    const marginPct = parseFiniteNumber(projectDebt.marginPct);
    const llcrCovenantVal = parseFiniteNumber(projectDebt.llcrCovenant);
    const plcrCovenantVal = parseFiniteNumber(projectDebt.plcrCovenant);
    const commitmentFeeVal = parseFiniteNumber(projectDebt.commitmentFeePct);
    const arrangementFeeVal = parseFiniteNumber(projectDebt.arrangementFeePct);
    const constructionMonthsVal = parseFiniteNumber(projectDebt.constructionMonths);
    const refinancingYearVal = parseFiniteNumber(projectDebt.refinancingYear);
    const cashSweepPctVal = parseFiniteNumber(projectDebt.cashSweepPct);

    return {
      debtPercent:
        debtPercent == null || debtPercent < 0 || debtPercent > 100
          ? "Debt Percent must be between 0 and 100."
          : "",
      tenorYears:
        tenorYears == null || tenorYears <= 0
          ? "Tenor must be greater than 0."
          : "",
      annualInterestRate:
        annualInterestRate != null && annualInterestRate < 0
          ? "Interest rate must be greater than or equal to 0."
          : "",
      moratoriumYears:
        moratoriumYears != null &&
        (moratoriumYears < 0 || (tenorYears != null && moratoriumYears > tenorYears))
          ? "Moratorium must be greater than or equal to 0 and no longer than tenor."
          : "",
      targetDscr:
        targetDscr == null || targetDscr <= 0
          ? "Target DSCR must be greater than 0."
          : "",
      minimumDscr:
        minimumDscr == null || minimumDscr <= 0
          ? "Minimum DSCR must be greater than 0."
          : targetDscr != null && targetDscr > 0 && minimumDscr >= targetDscr
            ? "Minimum DSCR covenant must be lower than the target DSCR."
            : "",
      dsraMonths:
        dsraMonths != null && dsraMonths < 0
          ? "DSRA months must be greater than or equal to 0."
          : "",
      bulletMaturityYear:
        bulletMaturityYear != null &&
        (bulletMaturityYear <= 0 || (tenorYears != null && bulletMaturityYear > tenorYears))
          ? "Bullet maturity year must be greater than 0 and no later than tenor."
          : "",
      balloonRepaymentPct:
        balloonRepaymentPct == null || balloonRepaymentPct < 0 || balloonRepaymentPct > 100
          ? "Balloon repayment must be between 0 and 100."
          : "",
      baseRatePct:
        baseRatePct != null && baseRatePct < 0
          ? "Base rate must be greater than or equal to 0."
          : "",
      marginPct:
        marginPct != null && marginPct < 0
          ? "Margin must be greater than or equal to 0."
          : "",
      llcrCovenant:
        llcrCovenantVal != null && llcrCovenantVal <= 0
          ? "LLCR covenant must be greater than 0."
          : "",
      plcrCovenant:
        plcrCovenantVal != null && plcrCovenantVal <= 0
          ? "PLCR covenant must be greater than 0."
          : "",
      commitmentFeePct:
        commitmentFeeVal != null && commitmentFeeVal < 0
          ? "Commitment fee must be non-negative."
          : "",
      arrangementFeePct:
        arrangementFeeVal != null && arrangementFeeVal < 0
          ? "Arrangement fee must be non-negative."
          : "",
      constructionMonths:
        constructionMonthsVal != null && constructionMonthsVal < 0
          ? "Construction period must be non-negative."
          : "",
      refinancingYear:
        refinancingYearVal != null && tenorYears != null && refinancingYearVal > tenorYears
          ? "Refinancing year must not exceed tenor."
          : "",
      cashSweepPct:
        projectDebt.cashSweepEnabled &&
        cashSweepPctVal != null &&
        (cashSweepPctVal < 0 || cashSweepPctVal > 100)
          ? "Cash sweep percentage must be between 0 and 100."
          : "",
    };
  }, [projectDebt]);

  const loanStructure = projectDebt.loanStructure || "sculpted";
  const isSculpted = loanStructure === "sculpted";
  const isBullet = loanStructure === "bullet";
  const requiresTargetDscr =
    isSculpted ||
    projectDebt.debtSizingMethod === "target_dscr_sizing" ||
    projectDebt.debtSizingMethod === "lower_of_fixed_percent_and_dscr";
  const hasMoratorium = Number(projectDebt.moratoriumYears || 0) > 0;
  const isFloatingInterest = projectDebt.interestType === "floating_base_plus_margin";
  // The percentage only binds in two of the three sizing methods. Under pure
  // target-DSCR sizing the engine takes the DSCR-sized amount and the only
  // ceiling is net CAPEX, so calling the field a leverage cap there promised a
  // limit that was never applied.
  const debtPercentApplies =
    projectDebt.debtSizingMethod !== "target_dscr_sizing";
  const debtPercentLabel =
    projectDebt.debtSizingMethod === "fixed_debt_percent"
      ? "Debt Percent"
      : debtPercentApplies
        ? "Max Debt Percent / Leverage Cap"
        : "Debt Percent (not applied)";
  const loanStructureNote =
    loanStructure === "sculpted"
      ? "Debt service is shaped from CFADS and target DSCR. The report should state whether debt amount is fixed or sized to target DSCR."
      : loanStructure === "annuity"
        ? "Total scheduled debt service remains approximately constant over the tenor."
        : loanStructure === "equal_principal"
          ? "Principal repayment is constant; total debt service declines over time."
          : loanStructure === "bullet"
            ? "Interest is paid during the term and principal is repaid at maturity. This creates refinancing or balloon risk."
            : "";

  return (
    <FeasibilityShell
      title="Economics"
      manual={{ settlementType: cfg?.settlementType, section: "economics" }}
      subtitle="Capture investment costs and financial parameters for the project."
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Box sx={tabsWrapperSx}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={tabsSx}
            variant="fullWidth"
          >
            <Tab
              value="investment"
              label="Investment Costs"
              sx={tabSx}
            />
            <Tab
              value="financial"
              label="Financial Parameters"
              sx={tabSx}
            />
          </Tabs>


          <TabPanel value="investment" current={activeTab}>
            <Box sx={{ px: { xs: 1, md: 1.5 }, pb: 1 }}>
              <Stack spacing={2}>

                <CostingMethodSelector
                  mode={costingMode}
                  isGreek={isGreek}
                  onChange={(next) => setFeasibility("economics.investmentCosts.costingMode", next)}
                  SubSection={SubSection}
                />
                {costingMode === "simplified" ? (
                  <SimplifiedCostingCard
                    isGreek={isGreek}
                    investmentCosts={investmentCosts}
                    updateInvestment={updateInvestment}
                    insurance={financialParams.insuranceRatePctYear}
                    updateInsurance={updateFinancial("insuranceRatePctYear")}
                    landLease={investmentCosts.landLeasingEurYear}
                    showPv={!isStandaloneBess}
                    showBattery={!isPvOnlyAsset}
                    pvExisting={pvIsExistingAsset}
                    installedKwp={installedKwp}
                    batteryKwh={activeBatteryEnergyKwh}
                    batteryName={activeBatteryName}
                    batteryOverride={batteryCostOverride}
                    LabeledField={LabeledField}
                    SubSection={SubSection}
                    gridSx={grid3Sx}
                  />
                ) : (
                <>
                <SubSection
                  title={
                    equipmentEditing
                      ? "Equipment Cost — editing"
                      : anyEquipmentOverride
                      ? "Equipment Cost — overridden"
                      : "Equipment Cost — from the Catalog (read-only)"
                  }
                  description={
                    equipmentEditing
                      ? `Enter a figure to override what the catalog says. Each one replaces the catalog value in the CAPEX hardware term AND in the basis for the % O&M and insurance rates, so one number means one thing. Leave a field blank to keep using the catalog. The total is always the sum and is never entered directly.`
                      : `Hardware purchase cost exactly as the feasibility CAPEX books it: catalog unit price × installed count (the "hybrid upfront cost" term). It is separate from — and added on top of — the installation ${EURO}/Wdc fields below. Change it in the catalog (PV System / BESS pages), or press Edit to override it for this study alone.`
                  }
                  action={
                    <Stack direction="row" spacing={1}>
                      {anyEquipmentOverride && (
                        <Button
                          size="small"
                          color="inherit"
                          onClick={clearEquipmentOverrides}
                        >
                          Use catalog
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={equipmentEditing ? "contained" : "outlined"}
                        onClick={() => setEquipmentEditing((on) => !on)}
                      >
                        {equipmentEditing ? "Done" : "Edit"}
                      </Button>
                    </Stack>
                  }
                >
                  {equipmentOverrideCleared.length > 0 && (
                    <Alert
                      severity="info"
                      onClose={() => setEquipmentOverrideCleared([])}
                    >
                      The equipment changed, so the cost override for the{" "}
                      {equipmentOverrideCleared.join("; ")} was dropped back to
                      the catalog price. Press Edit to enter a figure for the
                      new equipment.
                    </Alert>
                  )}
                  {anyEquipmentOverride && !equipmentEditing && (
                    <Alert severity="warning">
                      This study does not price its hardware at the catalog
                      value. The overridden figures below are what CAPEX books
                      and what the % O&M and insurance rates are charged on, and
                      the report's assumptions page states them as overrides.
                    </Alert>
                  )}
                  <Box sx={grid3Sx}>
                    {!isStandaloneBess && (
                      <TextField
                        fullWidth
                        id="field-econ-equipment-modules"
                        label={`PV Modules (${EURO})`}
                        InputLabelProps={{ shrink: true, sx: economicsInputLabelSx }}
                        sx={economicsFieldSx}
                        {...equipmentCostFieldProps({
                          valueKey: "modules",
                          nameKey: "moduleName",
                          currentName: equipmentModuleName,
                          catalogCost: moduleEquipmentCost,
                          overrideCost: moduleCostOverride,
                          baseHelper:
                            equipmentPrices.modulePrice == null
                              ? equipmentModuleName
                                ? `No catalog price found for ${equipmentModuleName}.`
                                : "Select a PV module on the PV System page."
                              : designCounts.modules > 0
                              ? `${designCounts.modules.toLocaleString("en-US")} modules × ${equipmentPrices.modulePrice.toLocaleString("en-US")} ${EURO} (${equipmentModuleName})${pvIsExistingAsset ? " — excluded from CAPEX (sunk asset); still the base for % O&M." : ""}`
                              : `Unit price ${equipmentPrices.modulePrice.toLocaleString("en-US")} ${EURO}/module — save the PV electrical design to compute the count (auto-design decides it at run time otherwise).`,
                        })}
                      />
                    )}
                    {!isStandaloneBess && (
                      <TextField
                        fullWidth
                        label={`Inverters (${EURO})`}
                        InputLabelProps={{ shrink: true, sx: economicsInputLabelSx }}
                        sx={economicsFieldSx}
                        {...equipmentCostFieldProps({
                          valueKey: "inverters",
                          nameKey: "inverterName",
                          currentName: equipmentInverterName,
                          catalogCost: inverterEquipmentCost,
                          overrideCost: inverterCostOverride,
                          baseHelper:
                            equipmentPrices.inverterPrice == null
                              ? equipmentInverterName
                                ? `No catalog price found for ${equipmentInverterName}.`
                                : "Select an inverter on the PV System page."
                              : designCounts.inverters > 0
                              ? `${designCounts.inverters.toLocaleString("en-US")} × ${equipmentPrices.inverterPrice.toLocaleString("en-US")} ${EURO} (${equipmentInverterName})${pvIsExistingAsset ? " — excluded from CAPEX (sunk asset); still the base for % O&M." : ""}`
                              : `Unit price ${equipmentPrices.inverterPrice.toLocaleString("en-US")} ${EURO}/unit — save the PV electrical design to compute the count.`,
                        })}
                      />
                    )}
                    {!isPvOnlyAsset && <TextField
                      fullWidth
                      label={`Battery System (${EURO})`}
                      InputLabelProps={{ shrink: true, sx: economicsInputLabelSx }}
                      sx={economicsFieldSx}
                      {...equipmentCostFieldProps({
                        valueKey: "battery",
                        nameKey: "batteryName",
                        currentName: activeBatteryName,
                        catalogCost: batteryEquipmentCost,
                        overrideCost: batteryCostOverride,
                        baseHelper:
                          equipmentPrices.batteryPrice == null
                            ? activeBatteryName
                              ? `No catalog price found for ${activeBatteryName}.`
                              : "Select a battery on the BESS page."
                            : `${activeBatteryCount} × ${equipmentPrices.batteryPrice.toLocaleString("en-US")} ${EURO} (${activeBatteryName})${otherSelectedBatteryCount > 0 ? ` — the other ${otherSelectedBatteryCount} selected model(s) run as alternative scenarios at their own catalog price.` : ""}`,
                      })}
                    />}
                    <TextField
                      fullWidth
                      label={`Total Equipment in CAPEX (${EURO})`}
                      value={formatEur(equipmentCapexTotal)}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true, sx: economicsInputLabelSx }}
                      sx={economicsFieldSx}
                      helperText={`Enters CAPEX as the hybrid upfront cost — before installation, indirect costs, contingency and grant.${pvIsExistingAsset ? " PV module & inverter hardware excluded (PV Asset Status: existing)." : ""}${anyEquipmentOverride ? " Includes the overridden figure(s) above, not the catalog price." : ""}`}
                    />
                  </Box>
                </SubSection>

                <SubSection
                  title="Installation Costs (EPC & Integration)"
                  description="Costs of installing and integrating each component (excluding equipment purchase). Labor & permits and contingency are entered here as a % of the installation cost."
                >
                  <Box sx={grid3Sx}>
                    <LabeledField
                      id="field-econ-pv-investment"
                      label={`PV Installation (${EURO}/Wdc)`}
                      type="number"
                      placeholder={investmentHints.moduleEurPerWdc.placeholder}
                      helperText={investmentHints.moduleEurPerWdc.helperText}
                      fullWidth
                      {...getInvestmentFieldProps("moduleEurPerWdc")}
                    />
                    <LabeledField
                      label={`Inverter Installation (${EURO}/W AC)`}
                      type="number"
                      placeholder={investmentHints.inverterEurPerWdc.placeholder}
                      helperText={investmentHints.inverterEurPerWdc.helperText}
                      fullWidth
                      {...getInvestmentFieldProps("inverterEurPerWdc")}
                    />
                    {!isPvOnlyAsset && <LabeledField
                      label={`BESS Installation (${EURO}/Whdc)`}
                      type="number"
                      value={investmentCosts.batteryEurPerWhdc}
                      onChange={updateInvestment("batteryEurPerWhdc")}
                      placeholder={investmentHints.batteryEurPerWhdc.placeholder}
                      helperText={investmentHints.batteryEurPerWhdc.helperText}
                      fullWidth
                    />}
                    <LabeledField
                      label={`Labor & Permits (% of subtotal)`}
                      type="number"
                      value={investmentCosts.laborPermitsPctSubtotal}
                      onChange={updateInvestment("laborPermitsPctSubtotal")}
                      placeholder={investmentHints.laborPermitsPctSubtotal.placeholder}
                      helperText={investmentHints.laborPermitsPctSubtotal.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Contingency (% of subtotal)`}
                      type="number"
                      value={investmentCosts.contingencyPctSubtotal}
                      onChange={updateInvestment("contingencyPctSubtotal")}
                      placeholder={investmentHints.contingencyPctSubtotal.placeholder}
                      helperText={investmentHints.contingencyPctSubtotal.helperText}
                      fullWidth
                    />
                  </Box>
                </SubSection>

                <SubSection
                  title="Balance of Plant & Other Works"
                  description="Grid connection, transformer, engineering, site works and electrical equipment."
                >
                  <Box sx={grid3Sx}>
                    <LabeledField
                      label={`Grid Interconnection (${EURO}/kWp)`}
                      type="number"
                      {...getInvestmentFieldProps("gridInterconnectionEurPerKwp")}
                      placeholder={investmentHints.gridInterconnectionEurPerKwp.placeholder}
                      helperText={investmentHints.gridInterconnectionEurPerKwp.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Transformer (${EURO}/kVA)`}
                      type="number"
                      value={investmentCosts.transformerEurPerKva}
                      onChange={updateInvestment("transformerEurPerKva")}
                      placeholder={investmentHints.transformerEurPerKva.placeholder}
                      helperText={investmentHints.transformerEurPerKva.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Design & Engineering (${EURO}/kWp)`}
                      type="number"
                      {...getInvestmentFieldProps("designEngineeringEurPerKwp")}
                      placeholder={investmentHints.designEngineeringEurPerKwp.placeholder}
                      helperText={investmentHints.designEngineeringEurPerKwp.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Mounting Structures (${EURO}/kWp)`}
                      type="number"
                      {...getInvestmentFieldProps("mountingStructuresEurPerKwp")}
                      placeholder={investmentHints.mountingStructuresEurPerKwp.placeholder}
                      helperText={investmentHints.mountingStructuresEurPerKwp.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Commissioning & Tests (${EURO}/kWp)`}
                      type="number"
                      {...getInvestmentFieldProps("commissioningTestsEurPerKwp")}
                      placeholder={investmentHints.commissioningTestsEurPerKwp.placeholder}
                      helperText={investmentHints.commissioningTestsEurPerKwp.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Cabling & Connections (${EURO}/kWp)`}
                      type="number"
                      {...getInvestmentFieldProps("cablingConnectionsEurPerKwp")}
                      placeholder={investmentHints.cablingConnectionsEurPerKwp.placeholder}
                      helperText={investmentHints.cablingConnectionsEurPerKwp.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Electrical Protection & Distribution (${EURO}/Site)`}
                      type="number"
                      value={investmentCosts.electricalProtectionEurSite}
                      onChange={updateInvestment("electricalProtectionEurSite")}
                      placeholder={investmentHints.electricalProtectionEurSite.placeholder}
                      helperText={investmentHints.electricalProtectionEurSite.helperText}
                      fullWidth
                    />
                    <LabeledField
                      label={`Monitoring & Metering (${EURO}/Site)`}
                      type="number"
                      value={investmentCosts.monitoringMeteringEurSite}
                      onChange={updateInvestment("monitoringMeteringEurSite")}
                      placeholder={investmentHints.monitoringMeteringEurSite.placeholder}
                      helperText={investmentHints.monitoringMeteringEurSite.helperText}
                      fullWidth
                    />
                  </Box>
                </SubSection>


                  <SubSection
                    title="Recurring Costs"
                    description="Annual O&M and lease-related cost assumptions."
                  >
                    <Box sx={gridSx}>
                      <LabeledField
                        id="field-econ-om"
                        label={`Module (%/Year)`}
                        type="number"
                        {...getInvestmentFieldProps("moduleRecurringPctYear")}
                        placeholder={investmentHints.moduleRecurringPctYear.placeholder}
                        helperText={investmentHints.moduleRecurringPctYear.helperText}
                        fullWidth
                      />
                      <LabeledField
                        label={`Inverter (%/Year)`}
                        type="number"
                        {...getInvestmentFieldProps("inverterRecurringPctYear")}
                        placeholder={investmentHints.inverterRecurringPctYear.placeholder}
                        helperText={investmentHints.inverterRecurringPctYear.helperText}
                        fullWidth
                      />
                      {!isPvOnlyAsset && <LabeledField
                        label={`Battery (%/Year)`}
                        type="number"
                        value={investmentCosts.batteryRecurringPctYear}
                        onChange={updateInvestment("batteryRecurringPctYear")}
                        placeholder={investmentHints.batteryRecurringPctYear.placeholder}
                        helperText={investmentHints.batteryRecurringPctYear.helperText}
                        fullWidth
                      />}
                      <LabeledField
                        id="field-econ-insurance"
                        label={`Insurance Rate (%/Year)`}
                        type="number"
                        value={financialParams.insuranceRatePctYear}
                        onChange={updateFinancial("insuranceRatePctYear")}
                        placeholder={investmentHints.insuranceRatePctYear.placeholder}
                        helperText={
                          isPvSunkAsset
                            ? "Annual insurance rate (% of project's CAPEX). PV Asset Status is 'Existing plant': CAPEX here is the storage addition only, so this covers the NEW asset — the existing PV plant is assumed to carry its own policy. Increase the rate if you want the full station's insurance burden in the cash flows."
                            : investmentHints.insuranceRatePctYear.helperText
                        }
                        fullWidth
                      />
                      <LabeledField
                        id="field-econ-land-lease"
                        label={`Land Leasing (${EURO}/Year)`}
                        type="number"
                        value={investmentCosts.landLeasingEurYear}
                        onChange={updateInvestment("landLeasingEurYear")}
                        placeholder={investmentHints.landLeasingEurYear.placeholder}
                        helperText={investmentHints.landLeasingEurYear.helperText}
                        fullWidth
                      />
                    </Box>
                  </SubSection>
                </>
                )}

              </Stack>
            </Box>
          </TabPanel>

          <TabPanel value="financial" current={activeTab}>
            <Stack spacing={2}>
              <SubSection
                title="Analysis Parameters"
                description="Core assumptions for inflation and discounting."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    id="field-econ-inflation"
                    label="Inflation Rate (%/Year)"
                    type="number"
                    value={financialParams.inflationRatePctYear}
                    onChange={updateFinancial("inflationRatePctYear")}
                    placeholder={financialHints.inflationRatePctYear.placeholder}
                    helperText={financialHints.inflationRatePctYear.helperText}
                    fullWidth
                  />

                  <LabeledField
                    id="field-econ-discount"
                    label="Real Discount Rate (%/Year)"
                    type="number"
                    value={financialParams.realDiscountRate}
                    onChange={updateFinancial("realDiscountRate")}
                    placeholder={financialHints.realDiscountRate.placeholder}
                    helperText="Rate excluding inflation."
                    fullWidth
                  />

                  <TextField
                    label="Nominal Discount Rate (%/Year)"
                    value={calculatedNominalDiscountPct != null
                      ? formatPercent(calculatedNominalDiscountPct)
                      : formatPercent(storedNominalDiscountPct)}
                    placeholder="Auto-calculated"
                    helperText={calculatedNominalDiscountPct == null && storedNominalDiscountPct != null
                      ? (isGreek
                        ? "Η τιμή που χρησιμοποιεί η μελέτη. Αν δώσετε πραγματικό επιτόκιο, υπολογίζεται από αυτό και τον πληθωρισμό."
                        : "The rate the study uses. Enter a real discount rate to calculate it from that and inflation.")
                      : "Automatically calculated from real discount rate and inflation."}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={economicsFieldSx}
                    InputLabelProps={{ sx: economicsInputLabelSx, shrink: true }}
                  />
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                    <strong>Real discount rate</strong> excludes inflation, while{" "}
                    <strong>nominal discount rate</strong> includes inflation.
                    <br />
                    Formula:{" "}
                    <strong>Nominal = (1 + Real) × (1 + Inflation) − 1</strong>
                    {calculatedNominalDiscountPct != null && (
                      <>
                        <br />
                        Based on current inputs, the calculated nominal discount rate is{" "}
                        <strong>{formatPercent(calculatedNominalDiscountPct)}%</strong>.
                      </>
                    )}
                  </Typography>
                </Box>
              </SubSection>

              <SubSection
                title="Merchant Revenue Projection"
                description="Use flat nominal for conservative screening, indexed/custom escalation when you have a long-term market view."
              >
                <Stack spacing={2}>
                  {/* Behind the meter nothing is re-dispatched against the price
                      path (bess_sizing skips the forward re-dispatch for BTM); the
                      path only prices a surplus credited at the day-ahead price. */}
                  {merchantProjectionMode === "forward_curve_redispatch" && isBtmSettlement(cfg?.settlementType) ? (
                    <Alert severity="info">
                      Behind the meter the battery is not re-dispatched against this price path:
                      the saving is valued at the retail tariff. The path is used only for a
                      surplus credited at the day-ahead price (net billing at the market price),
                      from year 2 on.
                    </Alert>
                  ) : merchantProjectionMode === "forward_curve_redispatch" ? (
                    <Alert severity="info">
                      Forward curve mode RE-RUNS the dispatch optimizer for selected forward
                      years on the level-scaled hourly price curve, so spread capture is
                      modelled rather than escalated. Expect a longer simulation (roughly one
                      extra dispatch pass per re-dispatched year).
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      Merchant revenue projection affects long-term financial projection only. It
                      does not rerun the dispatch optimizer.
                    </Alert>
                  )}
                  {/* Only a tolling contract with a floor has a trader top-up. */}
                  {cfg?.settlementType === "TollingFloorUpside" && (
                    <Alert severity="info">
                      If the tolling floor is binding, changing merchant revenue may affect trader
                      top-up more than project revenue during the contract term.
                    </Alert>
                  )}

                  <Box sx={gridSx}>
                    <LabeledField
                      label="Projection Mode"
                      select
                      value={merchantProjectionMode}
                      onChange={updateMerchantProjectionMode}
                      helperText={selectedMerchantProjectionMode.description}
                      fullWidth
                    >
                      {MERCHANT_REVENUE_PROJECTION_MODES.map((mode) => (
                        <MenuItem key={mode.value} value={mode.value}>
                          {mode.label}
                        </MenuItem>
                      ))}
                    </LabeledField>

                    {merchantProjectionMode === "inflation_indexed" && (
                      <TextField
                        label="Inflation Rate Used (%/Year)"
                        value={financialParams.inflationRatePctYear ?? ""}
                        helperText="Read from the financial assumptions above."
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={economicsFieldSx}
                        InputLabelProps={{ sx: economicsInputLabelSx, shrink: true }}
                      />
                    )}

                    {merchantProjectionMode === "custom_escalation" && (
                      <LabeledField
                        label="Annual Escalation (%/Year)"
                        type="number"
                        allowNegative
                        value={merchantRevenueProjection.annualEscalationPct}
                        onChange={updateMerchantAnnualEscalation}
                        placeholder="e.g. 2.0"
                        error={hasMerchantAnnualEscalationWarning}
                        helperText={
                          hasMerchantAnnualEscalationWarning
                            ? "Review annual escalation assumptions below -10% or above +10%."
                            : "Example: 2.0 grows merchant revenue by 2% per year."
                        }
                        fullWidth
                      />
                    )}
                    <DispatchForesightBasis
                      market={normalizeMarketZone(mergedCfg.biddingZone) || "GR"}
                      projectId={currentProject?.id}
                      factor={merchantRevenueProjection.dispatchForesightFactor}
                      provenance={merchantRevenueProjection.dispatchForesightProvenance}
                      onApply={(nextFactor, nextProvenance) =>
                        setMerchantRevenueProjection({
                          dispatchForesightFactor: nextFactor,
                          dispatchForesightProvenance: nextProvenance,
                        })
                      }
                      onClear={() =>
                        setMerchantRevenueProjection({
                          dispatchForesightProvenance: {
                            ...DEFAULT_DISPATCH_FORESIGHT_PROVENANCE,
                          },
                        })
                      }
                      onFactorChange={(raw) => {
                        const text = String(raw).replace(",", ".");
                        if (text !== "" && !/^\d*\.?\d*$/.test(text)) return;
                        setMerchantRevenueProjection({ dispatchForesightFactor: text });
                      }}
                      projectBatteryKw={activeBatteryPowerKw}
                      projectBatteryKwh={activeBatteryEnergyKwh}
                    />
                  </Box>

                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(merchantRevenueProjection.applyBatteryDegradation)}
                          onChange={(event) =>
                            setMerchantRevenueProjection({
                              applyBatteryDegradation: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Apply BESS degradation to merchant revenue"
                    />
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                      Reduces projected merchant revenue according to the modeled BESS capacity /
                      SOH trajectory where available. This is a simplified assumption and does not
                      rerun dispatch.
                    </Typography>
                  </Box>

                  {hasMissingMultiplierYears && (
                    <Alert severity="warning">
                      Custom multiplier curve has blank years. Missing years will use backend
                      fallback assumptions.
                    </Alert>
                  )}
                  {hasMissingRevenueYears && (
                    <Alert severity="warning">
                      Custom revenue curve has blank years. Missing years will use backend fallback
                      assumptions.
                    </Alert>
                  )}
                  {hasNegativeMultiplierYears && (
                    <Alert severity="error">Custom multipliers must be non-negative.</Alert>
                  )}
                  {hasNegativeRevenueYears && (
                    <Alert severity="error">Custom annual revenues must be non-negative.</Alert>
                  )}

                  {merchantProjectionMode === "custom_curve_multipliers" && (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Custom annual multipliers for {projectionYearCount} analysis years.
                      </Typography>
                      <MerchantRevenueCurveTable
                        label="multiplier"
                        valueLabel="Multiplier"
                        values={merchantRevenueProjection.customAnnualMultipliers}
                        valueKeys={MERCHANT_REVENUE_MULTIPLIER_KEYS}
                        yearCount={projectionYearCount}
                        onValueChange={updateMerchantMultiplierCurveValue}
                        startCalendarYear={firstProjectionCalendarYear}
                      />
                    </Box>
                  )}

                  {merchantProjectionMode === "custom_curve_revenues" && (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => setRevenueCurveExpanded((expanded) => !expanded)}
                        endIcon={
                          <ExpandMoreIcon
                            sx={{
                              transform: revenueCurveExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 160ms ease",
                            }}
                          />
                        }
                        sx={{
                          justifySelf: "flex-start",
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        Custom annual revenue curve
                      </Button>
                      <Collapse in={revenueCurveExpanded}>
                        <Box sx={{ display: "grid", gap: 1.5, pt: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Custom annual merchant revenue values for {projectionYearCount} analysis
                            years.
                          </Typography>
                          <MerchantRevenueCurveTable
                            label="revenue"
                            valueLabel={`Merchant Revenue (${EURO}/year)`}
                            values={merchantRevenueProjection.customAnnualRevenues}
                            valueKeys={MERCHANT_REVENUE_VALUE_KEYS}
                            yearCount={projectionYearCount}
                            onValueChange={updateMerchantRevenueCurveValue}
                            startCalendarYear={firstProjectionCalendarYear}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {merchantProjectionMode === "forward_curve_redispatch" && (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      {housePathYearMismatch && (
                        <Alert severity="warning">
                          {isGreek
                            ? `Η καμπύλη τιμών φορτώθηκε για έναρξη το ${forwardCurve.housePathStartYear}, ενώ το έργο ξεκινά το ${firstProjectionCalendarYear}. Φορτώστε ξανά την καμπύλη για το νέο έτος.`
                            : `The price path was loaded for a ${forwardCurve.housePathStartYear} start, but the project now starts in ${firstProjectionCalendarYear}. Reload the path for the new year.`}
                        </Alert>
                      )}
                      {housePathHeldYears > 0 && (
                        <Alert severity="info" data-testid="house-path-held-years">
                          {(() => {
                            const start = Number(forwardCurve.housePathStartYear) || firstProjectionCalendarYear;
                            const last = start + housePathHeldYears - 1;
                            const span = housePathHeldYears === 1 ? `${start}` : `${start}–${last}`;
                            const level = simYearLevel
                              ? (isGreek
                                ? ` (μέση τιμή ΑΕΗ ${simYearLevel.year}: ${simYearLevel.value.toFixed(2)} €/MWh)`
                                : ` (${simYearLevel.year} DAM average: ${simYearLevel.value.toFixed(2)} EUR/MWh)`)
                              : "";
                            return isGreek
                              ? `Το πρώτο συμβόλαιο αγοράς της καμπύλης είναι για το ${last + 1}. Για ${housePathHeldYears === 1 ? `το ${span}` : `τα έτη ${span}`} η μελέτη χρησιμοποιεί το επίπεδο τιμής του έτους προσομοίωσης${level}. Αν θέλετε άλλη τιμή για ${housePathHeldYears === 1 ? "αυτό το έτος" : "αυτά τα έτη"}, συμπληρώστε τη στον πίνακα.`
                              : `The path's first traded year is ${last + 1}. For ${span} the study uses the simulation-year price level${level}. To use another level for ${housePathHeldYears === 1 ? "that year" : "those years"}, enter it in the table.`;
                          })()}
                        </Alert>
                      )}
                      <Alert severity="info">
                        The annual price level path is an INPUT (e.g. an external / advisor
                        forward curve), not an in-house long-term forecast. The hourly shape
                        comes from the simulation year; intraday spreads scale proportionally
                        with the level, unless an annual spread compression is set below.
                        Blank years carry the previous value; leading blanks
                        use the simulation-year average price.
                      </Alert>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={async () => {
                            // Prefer the LIVE house path (anchors auto-ingested daily
                            // from the HEnEx derivatives Daily Official List); fall
                            // back to the static configuration when the endpoint or
                            // feed is unavailable.
                            let house = null;
                            // normalizeMarketZone, not a raw upper-case: a German
                            // project stores biddingZone "DE_LU" (the ENTSO-E zone
                            // name) while the house path is keyed by market code
                            // "DE", so the raw value missed the lookup and the
                            // button reported "unavailable (backend offline?)".
                            const pathZone = normalizeMarketZone(mergedCfg.biddingZone) || "GR";
                            try {
                              const baseUrl = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
                              const resp = await fetch(
                                `${baseUrl}/market/house-forward-path?years=${projectionYearCount}&startYear=${firstProjectionCalendarYear}&zone=${pathZone}`
                              );
                              if (resp.ok) house = await resp.json();
                            } catch (fetchError) {
                              house = null;
                            }
                            if (!house || !Array.isArray(house.levelsEurMwh) || !house.levelsEurMwh.length) {
                              // Static frontend mirror is GR-only; never mislabel
                              // a GR curve as another zone's path.
                              if (pathZone === "GR") {
                                house = buildHouseForwardPath(projectionYearCount, firstProjectionCalendarYear);
                              } else {
                                window.alert(
                                  `House forward path for ${pathZone} is unavailable (backend offline?). ` +
                                  "Enter the annual levels manually or retry."
                                );
                                return;
                              }
                            }
                            setForwardCurve({
                              levelInputMode: "levels_eur_mwh",
                              levelsEurMwh: house.levelsEurMwh,
                              levelSource: house.levelSource,
                              housePathStartYear: firstProjectionCalendarYear,
                            });
                          }}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Load MetEn house path (live anchors)
                        </Button>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {HOUSE_FORWARD_PATH.anchorSourceName} anchors + documented long-run
                          drift ({HOUSE_FORWARD_PATH.longRunDriftPctPerYear}%/yr, floor{" "}
                          {HOUSE_FORWARD_PATH.terminalFloorEurMwh} {EURO}/MWh).
                        </Typography>
                      </Box>
                      {HOUSE_FORWARD_PATH.status !== "market" && (
                        <Alert severity="warning">
                          The house path anchors are PLACEHOLDERS. Refresh them from the
                          EEX / HEnEx settlement screens (edit
                          src/components/config/houseForwardPath.js, set status to
                          &quot;market&quot;) before using this path in any external report.
                        </Alert>
                      )}
                      <Box sx={gridSx}>
                        <LabeledField
                          label="Curve Input Format"
                          select
                          value={forwardInputMode.value}
                          onChange={(event) =>
                            setForwardCurve({ levelInputMode: event.target.value })
                          }
                          helperText={forwardInputMode.description}
                          fullWidth
                        >
                          {FORWARD_LEVEL_INPUT_MODES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>
                        <LabeledField
                          label="Scenario"
                          select
                          value={forwardCurve.scenario}
                          onChange={(event) => setForwardCurve({ scenario: event.target.value })}
                          helperText="Low/High apply the delta below on top of the level path. Debt should be sized on a conservative scenario."
                          fullWidth
                        >
                          {FORWARD_CURVE_SCENARIOS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>
                        <LabeledField
                          label="Low Scenario Delta (%)"
                          type="number"
                          allowNegative
                          value={forwardCurve.lowDeltaPct}
                          onChange={(event) =>
                            setForwardCurve({ lowDeltaPct: event.target.value })
                          }
                          helperText="Applied to every year's level when Scenario = Low. e.g. -15."
                          fullWidth
                        />
                        <LabeledField
                          label="High Scenario Delta (%)"
                          type="number"
                          allowNegative
                          value={forwardCurve.highDeltaPct}
                          onChange={(event) =>
                            setForwardCurve({ highDeltaPct: event.target.value })
                          }
                          helperText="Applied to every year's level when Scenario = High. e.g. +15."
                          fullWidth
                        />
                        <LabeledField
                          label="Merchant Haircut (%)"
                          type="number"
                          value={forwardCurve.merchantHaircutPct}
                          onChange={(event) =>
                            setForwardCurve({ merchantHaircutPct: event.target.value })
                          }
                          helperText="Conservative haircut applied to the re-dispatched revenue schedule (bank-case convention). 0 = none."
                          fullWidth
                        />
                        <LabeledField
                          label="Annual Spread Compression (%)"
                          type="number"
                          value={forwardCurve.spreadCompressionPctYear ?? 0}
                          onChange={(event) =>
                            setForwardCurve({ spreadCompressionPctYear: event.target.value })
                          }
                          helperText="How much the gap between the expensive and the cheap hours of the same day narrows each year, as more batteries enter the market (they all charge in the same cheap hours and discharge in the same expensive ones). 0 = no extra narrowing: the gap follows only the price level (default). E.g. 3: each year the gap is 97% of the previous year's (chained) — about 89% in year 5, 76% in year 10 and 56% in year 20; with 2%: 92%, 83% and 68%. A day at 60 €/MWh at noon and 180 €/MWh in the evening (a gap of 120) would have a gap of about 67 €/MWh in year 20 at 3%. Before each re-dispatch year the hourly prices are narrowed around the year's average, so the optimizer re-decides when to charge and discharge, and on some days it no longer cycles; the annual price level is unchanged. The battery earns the gap minus its losses and wear, so its margin falls faster than the gap: this is usually the assumption that moves the NPV most. The report states the value used."
                          fullWidth
                        />
                        <LabeledField
                          label="Re-dispatch Against the Price Path"
                          select
                          value={forwardCurve.redispatchGranularity}
                          onChange={(event) =>
                            setForwardCurve({ redispatchGranularity: event.target.value })
                          }
                          helperText="Which years are re-optimized against the price levels above — representative years are 1, 5, 10, 15, 20 and the last, with the rest interpolated. This is the price axis only: re-dispatch at degraded capacity is set under BESS Details, and the two combine rather than replacing each other."
                          fullWidth
                        >
                          {FORWARD_REDISPATCH_GRANULARITIES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(merchantRevenueProjection.unifiedRedispatch)}
                                onChange={(event) =>
                                  setMerchantRevenueProjection({
                                    unifiedRedispatch: event.target.checked,
                                  })
                                }
                              />
                            }
                            label="Solve price and capacity in the same pass (recommended)"
                          />
                          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                            The years selected here and the capacity years under BESS Details are
                            merged into one list, and each year is optimized ONCE with that
                            year&apos;s price level, that year&apos;s battery state of health and
                            that year&apos;s PV output applied together. The result is that
                            year&apos;s revenue, so no capacity factor is applied on top of it.
                            Leave it off and the two axes are optimized separately and their
                            results multiplied — each of those results already contains the gain
                            the optimizer makes by re-arranging dispatch, so multiplying them
                            counts that gain twice. It also assumes the two axes are separable,
                            which they are not: a revenue stream paid at a fixed price competes
                            with the battery for the same connection capacity, so a lower price
                            level and a smaller battery interact. The two errors have opposite
                            signs and the net difference grows with the years, so it is measured
                            rather than assumed — the report states which of the two methods
                            produced the schedule. Solving once is also faster: a year that
                            appears on both lists costs one full-year run instead of two.
                          </Typography>
                        </Box>
                        <TextField
                          label="Level Path Source"
                          value={forwardCurve.levelSource ?? ""}
                          onChange={(event) =>
                            setForwardCurve({ levelSource: event.target.value })
                          }
                          placeholder='e.g. "Green Value forward curve 2026-07" or "Advisor X central case"'
                          helperText="Shown in the report's methodology disclosure."
                          fullWidth
                          sx={economicsFieldSx}
                          InputLabelProps={{ sx: economicsInputLabelSx, shrink: true }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Annual price path for {projectionYearCount} analysis years, expressed as{" "}
                        <span>{forwardInputMode.inlineLabel || forwardInputMode.label}</span>{" "}
                        — paste an external curve here.
                        The scenario Low/High delta applies uniformly ON TOP of this curve, and
                        the merchant haircut applies to the resulting revenue schedule regardless
                        of the input format.
                      </Typography>
                      <MerchantRevenueCurveTable
                        label="forward price level"
                        valueLabel={forwardInputMode.columnLabel}
                        values={forwardCurve[forwardCurveArrayKey]}
                        valueKeys={FORWARD_LEVEL_KEYS}
                        yearCount={projectionYearCount}
                        onValueChange={updateForwardLevelCurveValue}
                        allowNegative={forwardCurveAllowsNegative}
                        startCalendarYear={firstProjectionCalendarYear}
                        blankPlaceholder={
                          forwardCurveArrayKey === "levelsEurMwh"
                            ? (simYearLevel
                              ? (isGreek
                                ? `${simYearLevel.value.toFixed(2)} · μέση τιμή ΑΕΗ ${simYearLevel.year}`
                                : `${simYearLevel.value.toFixed(2)} · ${simYearLevel.year} DAM average`)
                              : (isGreek ? "Επίπεδο έτους προσομοίωσης" : "Simulation-year level"))
                            : null
                        }
                      />
                    </Box>
                  )}

                  <TextField
                    label="Notes"
                    value={merchantRevenueProjection.notes ?? ""}
                    onChange={(event) =>
                      setMerchantRevenueProjection({ notes: event.target.value })
                    }
                    placeholder="Optional projection assumptions or source notes"
                    multiline
                    minRows={2}
                    fullWidth
                    sx={economicsFieldSx}
                    InputLabelProps={{ sx: economicsInputLabelSx, shrink: true }}
                  />
                </Stack>
              </SubSection>

              <SubSection
                title="Financial Assumptions"
                description="Project-level tax and terminal value assumptions."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    id="field-econ-income-tax"
                    label="National Income Tax (%)"
                    type="number"
                    value={financialParams.incomeTax}
                    onChange={updateFinancial("incomeTax")}
                    placeholder={financialHints.incomeTax.placeholder}
                    helperText={financialHints.incomeTax.helperText}
                    fullWidth
                  />

                  <LabeledField
                    id="field-econ-salvage"
                    label="Net Salvage Value (%)"
                    type="number"
                    value={financialParams.salvageValue}
                    onChange={updateFinancial("salvageValue")}
                    placeholder={financialHints.salvageValue.placeholder}
                    helperText={financialHints.salvageValue.helperText}
                    fullWidth
                  />
                </Box>
              </SubSection>

              <SubSection
                title="Incentives & Subsidies"
                description="Public support and revenue-side incentive assumptions."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    id="field-econ-grant"
                    label="Capex Grant (%)"
                    type="number"
                    value={financialParams.capexGrantPct}
                    onChange={updateFinancial("capexGrantPct")}
                    placeholder={financialHints.capexGrantPct.placeholder}
                    helperText={
                      <>
                        Up-front subsidy on eligible CAPEX.
                        <br />
                        The grant is treated as non-taxable income on the year received.
                      </>
                    }
                    fullWidth
                  />

                  <LabeledField
                    id="field-econ-incentive"
                    label={`Generation Incentive (${EURO}/kWh)`}
                    type="number"
                    value={financialParams.generationIncentiveEurPerKwh}
                    onChange={updateFinancial("generationIncentiveEurPerKwh")}
                    placeholder={financialHints.generationIncentiveEurPerKwh.placeholder}
                    helperText={financialHints.generationIncentiveEurPerKwh.helperText}
                    fullWidth
                  />
                </Box>
              </SubSection>

              {/* Battery augmentation: SET on the BESS page, next to the mode
                  that decides whether a top-up happens at all. Shown here as a
                  cost line, because splitting the decision from its price is
                  what allowed capacity to be restored with nothing paying for
                  it. */}
              {!isPvOnlyAsset && <SubSection
                title="Battery Augmentation"
                description="A mid-life capacity top-up, booked as a CAPEX outflow in its year. Set it on the BESS page, under Battery Performance → Augmentation, where the year and the cost sit beside the mode that governs them."
              >
                {(() => {
                  const augYear = Number(financialParams.augmentationYear) || 0;
                  const augCost = Number(financialParams.augmentationCostEur) || 0;
                  const augMode = String(
                    cfg?.batteryPerformance?.augmentationMode ?? "manual");
                  if (augMode === "fom_pct") {
                    return (
                      <Typography sx={{ fontSize: "0.845rem", color: "text.secondary" }}>
                        A continuous allowance of{" "}
                        <b>{Number(cfg?.batteryPerformance?.augmentationFomPctYear ?? 2.5)}% of
                        battery CAPEX per year</b> is charged inside fixed O&amp;M, keeping
                        capacity at nameplate. There is no separate outflow to book here.
                      </Typography>
                    );
                  }
                  if (augCost > 0) {
                    return (
                      <Typography sx={{ fontSize: "0.845rem", color: "text.secondary" }}>
                        <b>{EURO}{augCost.toLocaleString("en-US")}</b>
                        {augMode === "trigger"
                          ? " in the year the retention curve reaches the trigger"
                          : augYear > 0
                          ? ` in project year ${augYear}`
                          : ""}
                        , booked undiscounted; the cash-flow model discounts it. Capacity is
                        restored from that year on.
                      </Typography>
                    );
                  }
                  return (
                    <Typography sx={{ fontSize: "0.845rem", color: "text.secondary" }}>
                      No augmentation outlay is configured, so no capacity restoration is
                      modelled — the battery degrades along its curve for the whole horizon.
                    </Typography>
                  );
                })()}
              </SubSection>}

              <SubSection
                title="Downside / P90 Assumptions"
                description="Uncertainty budget for the report's P50 / P90 / P99 exceedance test, the probability-of-breach view and the Monte-Carlo simulation. Combined in quadrature; the price/revenue term is scaled by merchant exposure, so contracted (PPA / tolling) cases carry little price risk. Behind-the-meter runs use the retail-tariff term below instead (an avoided cost carries tariff risk, not wholesale price risk) and measure the load-volume term by re-dispatch when a load uncertainty band is configured. Conservative defaults are fine for most cases."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    label="Resource / Yield Uncertainty (%)"
                    type="number"
                    value={financialParams.p90ResourceUncertaintyPct}
                    onChange={updateFinancial("p90ResourceUncertaintyPct")}
                    placeholder="4"
                    helperText="Inter-annual resource / production variability (P50 to P90)."
                    fullWidth
                  />

                  <LabeledField
                    label="Price / Revenue Uncertainty (%)"
                    type="number"
                    value={financialParams.p90PriceUncertaintyPct}
                    onChange={updateFinancial("p90PriceUncertaintyPct")}
                    placeholder="15"
                    helperText="Merchant price / revenue uncertainty; scaled by merchant exposure in the report."
                    fullWidth
                  />

                  <LabeledField
                    label="Modelling Uncertainty (%)"
                    type="number"
                    value={financialParams.p90ModelUncertaintyPct}
                    onChange={updateFinancial("p90ModelUncertaintyPct")}
                    placeholder="5"
                    helperText="Simulation / modelling uncertainty; applies across all revenue models."
                    fullWidth
                  />

                  <LabeledField
                    label="Retail Tariff Uncertainty (%) - BtM only"
                    type="number"
                    value={financialParams.btmTariffUncertaintyPct ?? "10"}
                    onChange={updateFinancial("btmTariffUncertaintyPct")}
                    placeholder="10"
                    helperText="Behind-the-meter savings are an avoided cost, so the price term is the retail tariff path (not the wholesale market, and not scaled by merchant exposure). Applied to the tariff-valued share of the saving; the exported-surplus share takes the price/revenue term above. Ignored on every other settlement."
                    fullWidth
                  />
                </Box>

                {cfg.feasibility?.pvRisk?.enabled && <Typography sx={{ my: 1, fontWeight: 600 }}>
                  {isGreek ? "Στην εκτέλεση προσομοίωσης έχει επιλεγεί η ενσωμάτωση της ιστορικής παραγωγής ΦΒ. Αυτή αντικαθιστά την παρακάτω γενική Monte Carlo και τα ποσοστά αβεβαιότητας δυναμικού/μοντέλου για τη συγκεκριμένη εκτέλεση. Οι αποθηκευμένες γενικές παραδοχές διατηρούνται για εκτελέσεις χωρίς ενσωμάτωση ΦΒ." : "Historical PV integration is selected on the simulation page. For this run it replaces the generic Monte Carlo below and the resource/model uncertainty percentages. Your generic assumptions remain saved for runs without PV integration."}
                </Typography>}
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={Boolean(cfg.feasibility?.pvRisk?.enabled)}
                        checked={Boolean(financialParams.stochasticSimulationEnabled)}
                        onChange={(event) =>
                          setFeasibility(
                            "economics.financialParams.stochasticSimulationEnabled",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Monte-Carlo revenue simulation (seeded, reproducible)"
                  />
                  <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 1 }}>
                    ON by default (recommended for bank-facing reports): seeded AR(1) annual
                    revenue-level paths whose per-year spread equals the uncertainty budget
                    above; adds P(NPV&lt;0), P(DSCR&lt;covenant) and percentile bands. Additive
                    only - no base number changes; switch OFF to omit the exhibit. Dispatch is
                    not re-optimised per path (stated in the report).
                  </Typography>
                  <Box sx={gridSx}>
                    <LabeledField
                      label="Year-to-Year Correlation (0-1)"
                      type="number"
                      value={financialParams.stochasticYearCorrelation ?? "0.7"}
                      onChange={updateFinancial("stochasticYearCorrelation")}
                      placeholder="0.7"
                      helperText="Serial correlation of annual revenue levels (house assumption, labelled in the report). 1 = one systematic shock (matches the analytic view); 0 = independent years."
                      fullWidth
                    />
                    <LabeledField
                      label="Simulation Paths"
                      type="number"
                      disabled={Boolean(cfg.feasibility?.pvRisk?.enabled)}
                      value={financialParams.stochasticPaths ?? "2000"}
                      onChange={updateFinancial("stochasticPaths")}
                      placeholder="2000"
                      helperText={cfg.feasibility?.pvRisk?.enabled
                        ? (isGreek ? "Η ενσωματωμένη Monte Carlo χρησιμοποιεί το πλήθος διαδρομών της ολοκληρωμένης ανάλυσης ΦΒ. Η τιμή εδώ διατηρείται για τη γενική οικονομική Monte Carlo." : "Integrated Monte Carlo uses the path count from the completed PV analysis. This value is retained for the generic financial Monte Carlo.")
                        : "Number of seeded paths (200-20,000). 2,000 keeps the sampling error on a 10% probability near +/-0.7%."}
                      fullWidth
                    />
                  </Box>
                </Box>
              </SubSection>

              <SubSection
                title="M&A / Asking Price (optional)"
                description="Leave 0 for no M&A view (default - nothing changes). When set, the report adds an indicative buyer view: premium/(discount) vs the model value of the same after-tax cash flows, buyer unlevered IRR, and - when debt is configured - buyer equity IRR with the in-place debt assumed at face (share-deal convention, stated in the report)."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    label="Asking Price (EUR)"
                    type="number"
                    value={financialParams.askingPriceEur ?? "0"}
                    onChange={updateFinancial("askingPriceEur")}
                    placeholder="0"
                    helperText="Price for 100% of the project SPV. 0 = the exhibit does not appear."
                    fullWidth
                  />
                </Box>
              </SubSection>

              {!isPvOnlyAsset && <SubSection
                title="Revenue Quality (balancing responsibility)"
                description="Plant-level adjustments a balancing-responsible party (FoSE) carries. Neutral defaults (100% / 0) change nothing. Availability derates the merchant settlement value (merchant / 11A / tolling merchant basis), the PPA delivered volume and the battery-driven share of BtM savings; the imbalance charge applies per delivered MWh of the WHOLE plant injection (PV + battery)."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    label="BESS Availability (%)"
                    type="number"
                    value={financialParams.bessAvailabilityPct ?? "100"}
                    onChange={updateFinancial("bessAvailabilityPct")}
                    placeholder="100"
                    helperText="Battery availability applied to the settlement value, AFTER the dispatch has been scheduled. 100 = neutral, and neutral is right whenever the Forced Outage Rate / Planned Outage hours on the Battery Performance page are set - those already derate the dispatch itself, and the two multiply."
                    fullWidth
                  />

                  <LabeledField
                    label="PV / Plant Availability (%)"
                    type="number"
                    value={financialParams.pvAvailabilityPct ?? ""}
                    onChange={updateFinancial("pvAvailabilityPct")}
                    placeholder="same as BESS"
                    helperText="Leave blank to keep the previous behaviour (the BESS figure derates the whole settlement). Set it where the two legs are separable - under Category 11A the PV injection settles at the regulated Reference Price and a battery outage does not stop it, so a battery availability of 97% should not cut the PV revenue by 3%."
                    fullWidth
                  />

                  <LabeledField
                    label="Imbalance Cost (EUR/MWh)"
                    type="number"
                    value={financialParams.imbalanceCostEurPerMwh ?? "0"}
                    onChange={updateFinancial("imbalanceCostEurPerMwh")}
                    placeholder="0"
                    helperText="Deviation / imbalance settlement cost per delivered MWh of total plant injection (BRP view). 0 = neutral. Applies to merchant-exposed revenue (merchant / 11A / tolling merchant basis)."
                    fullWidth
                  />
                </Box>

                {availabilityIssue ? (
                  <Alert severity={availabilityIssue.severity} sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {availabilityIssue.headline}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {availabilityIssue.detail}
                    </Typography>
                    {availabilityIssue.fix ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        sx={{ mt: 1.25 }}
                        onClick={() =>
                          setFeasibility(
                            `economics.financialParams.${availabilityIssue.fix.field}`,
                            availabilityIssue.fix.value
                          )
                        }
                      >
                        {availabilityIssue.fix.label}
                      </Button>
                    ) : null}
                  </Alert>
                ) : null}
              </SubSection>}

              <SubSection
                title="Corporate Tax - Greece (labelled assumptions)"
                description="OFF = legacy flat cash tax on positive yearly cash flow (unchanged numbers). ON = Greek tax engine: straight-line tax depreciation (law 4172/2013 art. 24) and tax-loss carry-forward (art. 27) at the National Income Tax rate entered above. No interest deduction is taken (art. 49 ATAD not modelled - conservative: a levered SPV pays less tax than shown). All parameters print in the report as labelled assumptions - confirm with a tax advisor before external reliance."
              >
                <Box sx={gridSx}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(financialParams.taxEngineEnabled)}
                          onChange={(event) =>
                            setFeasibility(
                              "economics.financialParams.taxEngineEnabled",
                              event.target.checked
                            )
                          }
                        />
                      }
                      label="Enable Greek tax engine (depreciation + loss carry-forward)"
                    />
                  </Box>

                  <LabeledField
                    label="PV Depreciation Rate (%/yr)"
                    type="number"
                    value={financialParams.pvDepreciationRatePct ?? "10"}
                    onChange={updateFinancial("pvDepreciationRatePct")}
                    placeholder="10"
                    helperText="Straight-line, on the PV share of the net-of-grant CAPEX. Machinery & equipment class: 10%/yr (art. 24). Use 4% for any civil-works share."
                    fullWidth
                  />

                  {!isPvOnlyAsset && <LabeledField
                    label="BESS Depreciation Rate (%/yr)"
                    type="number"
                    value={financialParams.bessDepreciationRatePct ?? "10"}
                    onChange={updateFinancial("bessDepreciationRatePct")}
                    placeholder="10"
                    helperText="Straight-line, on the BESS share of the net-of-grant CAPEX. Machinery & equipment class: 10%/yr (art. 24)."
                    fullWidth
                  />}

                  <LabeledField
                    label="Loss Carry-Forward (years)"
                    type="number"
                    value={financialParams.lossCarryForwardYears ?? "5"}
                    onChange={updateFinancial("lossCarryForwardYears")}
                    placeholder="5"
                    helperText="Greek limit: 5 consecutive years, no carry-back (art. 27). Values above 5 are kept but flagged in the report."
                    fullWidth
                  />
                </Box>
              </SubSection>

              <SubSection
                title="Project Term Debt"
                description="Debt sizing and repayment structure assumptions."
              >
                <Box sx={gridSx}>
                  <LabeledField
                    label="Loan Structure"
                    select
                    value={loanStructure}
                    onChange={updateProjectDebt("loanStructure")}
                    helperText={financialHints.loanStructure.helperText}
                    fullWidth
                  >
                    <MenuItem value="sculpted">Sculpted (DSCR based)</MenuItem>
                    <MenuItem value="annuity">Annuity (constant payment)</MenuItem>
                    <MenuItem value="equal_principal">Equal Principal</MenuItem>
                    <MenuItem value="bullet">Bullet</MenuItem>
                  </LabeledField>

                  <LabeledField
                    label="Debt Sizing Method"
                    select
                    value={projectDebt.debtSizingMethod}
                    onChange={updateProjectDebt("debtSizingMethod")}
                    helperText={financialHints.debtSizingMethod.helperText}
                    fullWidth
                  >
                    {DEBT_SIZING_METHOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </LabeledField>

                  <LabeledField
                    id="field-econ-debt-percent"
                    label={debtPercentLabel}
                    type="number"
                    value={projectDebt.debtPercent}
                    onChange={updateProjectDebt("debtPercent")}
                    placeholder={financialHints.debtPercent.placeholder}
                    disabled={!debtPercentApplies}
                    error={Boolean(debtValidation.debtPercent) && debtPercentApplies}
                    helperText={
                      (debtPercentApplies && debtValidation.debtPercent) ||
                      (projectDebt.debtSizingMethod === "fixed_debt_percent"
                        ? "Debt share of total CAPEX."
                        : debtPercentApplies
                          ? "Ceiling on leverage: the debt taken is the lower of this share and the DSCR-sized amount."
                          : "Not applied with this sizing method — the amount comes from the target DSCR and the only ceiling is net CAPEX. To cap leverage, choose “Lower of fixed debt percent and DSCR-sized debt”.")
                    }
                    fullWidth
                  />

                  <LabeledField
                    label="Tenor (Years)"
                    type="number"
                    value={projectDebt.tenorYears}
                    onChange={updateProjectDebt("tenorYears")}
                    placeholder={financialHints.tenorYears.placeholder}
                    error={Boolean(debtValidation.tenorYears)}
                    helperText={debtValidation.tenorYears || financialHints.tenorYears.helperText}
                    fullWidth
                  />

                  <LabeledField
                    label="Annual Interest Rate (%)"
                    type="number"
                    value={projectDebt.annualInterestRatePct}
                    onChange={updateProjectDebt("annualInterestRatePct")}
                    placeholder={financialHints.annualInterestRatePct.placeholder}
                    error={Boolean(debtValidation.annualInterestRate)}
                    helperText={
                      debtValidation.annualInterestRate ||
                      financialHints.annualInterestRatePct.helperText
                    }
                    fullWidth
                  />

                  <LabeledField
                    label="Interest Type"
                    select
                    value={projectDebt.interestType}
                    onChange={updateProjectDebt("interestType")}
                    helperText={financialHints.interestType.helperText}
                    fullWidth
                  >
                    {INTEREST_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </LabeledField>

                  {isFloatingInterest && (
                    <>
                      <LabeledField
                        label="Base Rate (%)"
                        type="number"
                        value={projectDebt.baseRatePct ?? ""}
                        onChange={updateProjectDebt("baseRatePct")}
                        placeholder={financialHints.baseRatePct.placeholder}
                        error={Boolean(debtValidation.baseRatePct)}
                        helperText={debtValidation.baseRatePct || financialHints.baseRatePct.helperText}
                        fullWidth
                      />
                      <LabeledField
                        label="Margin (%)"
                        type="number"
                        value={projectDebt.marginPct ?? ""}
                        onChange={updateProjectDebt("marginPct")}
                        placeholder={financialHints.marginPct.placeholder}
                        error={Boolean(debtValidation.marginPct)}
                        helperText={debtValidation.marginPct || financialHints.marginPct.helperText}
                        fullWidth
                      />
                    </>
                  )}

                  <LabeledField
                    label="Moratorium (Years)"
                    type="number"
                    value={projectDebt.moratoriumYears}
                    onChange={updateProjectDebt("moratoriumYears")}
                    placeholder={financialHints.moratoriumYears.placeholder}
                    error={Boolean(debtValidation.moratoriumYears)}
                    helperText={debtValidation.moratoriumYears || financialHints.moratoriumYears.helperText}
                    fullWidth
                  />

                  {hasMoratorium && (
                    <LabeledField
                      label="Moratorium Interest Treatment"
                      select
                      value={projectDebt.moratoriumInterestTreatment}
                      onChange={updateProjectDebt("moratoriumInterestTreatment")}
                      helperText={financialHints.moratoriumInterestTreatment.helperText}
                      fullWidth
                    >
                      {MORATORIUM_INTEREST_TREATMENT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </LabeledField>
                  )}

                  {requiresTargetDscr && (
                    <LabeledField
                      label="Target DSCR"
                      type="number"
                      value={projectDebt.targetDscr}
                      onChange={updateProjectDebt("targetDscr")}
                      placeholder={financialHints.targetDSCR.placeholder}
                      error={Boolean(debtValidation.targetDscr)}
                      helperText={debtValidation.targetDscr || financialHints.targetDSCR.helperText}
                      fullWidth
                    />
                  )}

                  <LabeledField
                    label="Minimum DSCR Covenant"
                    type="number"
                    value={projectDebt.minimumDscrCovenant}
                    onChange={updateProjectDebt("minimumDscrCovenant")}
                    placeholder={financialHints.minimumDscrCovenant.placeholder}
                    error={Boolean(debtValidation.minimumDscr)}
                    helperText={
                      debtValidation.minimumDscr || financialHints.minimumDscrCovenant.helperText
                    }
                    fullWidth
                  />

                  <LabeledField
                    label="Debt Service Frequency"
                    select
                    value={projectDebt.debtServiceFrequency}
                    onChange={updateProjectDebt("debtServiceFrequency")}
                    helperText={financialHints.debtServiceFrequency.helperText}
                    fullWidth
                  >
                    {DEBT_SERVICE_FREQUENCY_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={option.value !== "annual"}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </LabeledField>

                  <LabeledField
                    label="DSRA Months"
                    type="number"
                    value={projectDebt.dsraMonths ?? ""}
                    onChange={updateProjectDebt("dsraMonths")}
                    placeholder={financialHints.dsraMonths.placeholder}
                    error={Boolean(debtValidation.dsraMonths)}
                    helperText={debtValidation.dsraMonths || financialHints.dsraMonths.helperText}
                    fullWidth
                  />

                  <LabeledField
                    label="DSRA Funding Method"
                    select
                    value={projectDebt.dsraFundingMethod}
                    onChange={updateProjectDebt("dsraFundingMethod")}
                    helperText={financialHints.dsraFundingMethod.helperText}
                    fullWidth
                  >
                    {DSRA_FUNDING_METHOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </LabeledField>

                  {/* Sculpting Mode is deliberately not offered: it duplicated
                      the Debt Sizing Method above and nothing in the engine ever
                      read it, so a study could be configured through a control
                      that changed no result. Whether the amount is fixed or
                      sized to the target DSCR is set in one place only. */}

                  {isBullet && (
                    <>
                      <LabeledField
                        label="Bullet Maturity Year"
                        type="number"
                        value={projectDebt.bulletMaturityYear ?? ""}
                        onChange={updateProjectDebt("bulletMaturityYear")}
                        placeholder={financialHints.bulletMaturityYear.placeholder}
                        error={Boolean(debtValidation.bulletMaturityYear)}
                        helperText={
                          debtValidation.bulletMaturityYear ||
                          financialHints.bulletMaturityYear.helperText
                        }
                        fullWidth
                      />
                      <LabeledField
                        label="Balloon Repayment (% of principal)"
                        type="number"
                        value={projectDebt.balloonRepaymentPct}
                        onChange={updateProjectDebt("balloonRepaymentPct")}
                        placeholder={financialHints.balloonRepaymentPct.placeholder}
                        error={Boolean(debtValidation.balloonRepaymentPct)}
                        helperText={
                          debtValidation.balloonRepaymentPct ||
                          "Bullet debt is interest-only until the selected maturity year, when the balloon principal is repaid."
                        }
                        fullWidth
                      />
                    </>
                  )}

                  <LabeledField
                    label={`Debt Closing Costs (${EURO})`}
                    type="number"
                    value={projectDebt.debtClosingCostsEur ?? ""}
                    onChange={updateProjectDebt("debtClosingCostsEur")}
                    placeholder={financialHints.debtClosingCostsEur.placeholder}
                    helperText={financialHints.debtClosingCostsEur.helperText}
                    fullWidth
                  />

                  <LabeledField
                    label="Up-front Fee (%)"
                    type="number"
                    value={projectDebt.upfrontFeePct ?? ""}
                    onChange={updateProjectDebt("upfrontFeePct")}
                    placeholder={financialHints.upFrontFeePct.placeholder}
                    helperText={financialHints.upFrontFeePct.helperText}
                    fullWidth
                  />
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", letterSpacing: "0.03em" }}
                  >
                    Selected structure
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.35,
                      color: "text.primary",
                      fontWeight: 600,
                      lineHeight: 1.55,
                    }}
                  >
                    {loanStructureNote}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "primary.main",
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(33,150,243,0.07)"
                        : "rgba(33,150,243,0.05)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em" }}
                  >
                    Covered debt structures
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.4, color: "text.secondary", lineHeight: 1.55 }}
                  >
                    PYTHIA currently supports the main repayment profiles used in BESS project
                    finance: sculpted / DSCR-based debt, annuity debt, equal principal debt, and
                    bullet debt. Additional facility types such as construction loans, mini-perms,
                    bridge loans, mezzanine debt, and shareholder loans are treated as lender
                    assumptions or disclosure unless explicitly modelled.
                  </Typography>
                </Box>

                <Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setAdvancedLenderExpanded((prev) => !prev)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: advancedLenderExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      />
                    }
                    sx={{
                      color: "text.secondary",
                      textTransform: "none",
                      px: 0,
                      fontSize: "0.82rem",
                    }}
                  >
                    Advanced Lender Assumptions
                  </Button>
                  <Collapse in={advancedLenderExpanded}>
                    <Stack spacing={2} sx={{ mt: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", lineHeight: 1.55 }}
                      >
                        These fields support bankability disclosure and lender review. They are not
                        financial adjustments unless explicitly modelled by the backend.
                      </Typography>
                      <Box sx={gridSx}>
                        <LabeledField
                          label="Facility Type"
                          select
                          value={projectDebt.facilityType}
                          onChange={updateProjectDebt("facilityType")}
                          helperText={financialHints.facilityType.helperText}
                          fullWidth
                        >
                          {FACILITY_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        <LabeledField
                          label="Facility Status"
                          select
                          value={projectDebt.facilityStatus}
                          onChange={updateProjectDebt("facilityStatus")}
                          helperText={financialHints.facilityStatus.helperText}
                          fullWidth
                        >
                          {FACILITY_STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        <LabeledField
                          label="Security Package Status"
                          select
                          value={projectDebt.securityPackageStatus}
                          onChange={updateProjectDebt("securityPackageStatus")}
                          helperText={financialHints.securityPackageStatus.helperText}
                          fullWidth
                        >
                          {SECURITY_PACKAGE_STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        <LabeledField
                          label="Construction Drawdown Mode"
                          select
                          value={projectDebt.constructionDrawdownMode}
                          onChange={updateProjectDebt("constructionDrawdownMode")}
                          helperText={financialHints.constructionDrawdownMode.helperText}
                          fullWidth
                        >
                          {CONSTRUCTION_DRAWDOWN_MODE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        {projectDebt.constructionDrawdownMode !== "not_modelled" && (
                          <LabeledField
                            label="Construction Period (Months)"
                            type="number"
                            value={projectDebt.constructionMonths ?? ""}
                            onChange={updateProjectDebt("constructionMonths")}
                            placeholder={financialHints.constructionMonths.placeholder}
                            error={Boolean(debtValidation.constructionMonths)}
                            helperText={
                              debtValidation.constructionMonths ||
                              financialHints.constructionMonths.helperText
                            }
                            fullWidth
                          />
                        )}

                        <LabeledField
                          label="Interest During Construction Treatment"
                          select
                          value={projectDebt.interestDuringConstructionTreatment}
                          onChange={updateProjectDebt("interestDuringConstructionTreatment")}
                          helperText={financialHints.interestDuringConstructionTreatment.helperText}
                          fullWidth
                        >
                          {INTEREST_DURING_CONSTRUCTION_TREATMENT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        <LabeledField
                          label="Commitment Fee (%)"
                          type="number"
                          value={projectDebt.commitmentFeePct ?? ""}
                          onChange={updateProjectDebt("commitmentFeePct")}
                          placeholder={financialHints.commitmentFeePct.placeholder}
                          error={Boolean(debtValidation.commitmentFeePct)}
                          helperText={
                            debtValidation.commitmentFeePct ||
                            financialHints.commitmentFeePct.helperText
                          }
                          fullWidth
                        />

                        <LabeledField
                          label="Arrangement Fee (%)"
                          type="number"
                          value={projectDebt.arrangementFeePct ?? ""}
                          onChange={updateProjectDebt("arrangementFeePct")}
                          placeholder={financialHints.arrangementFeePct.placeholder}
                          error={Boolean(debtValidation.arrangementFeePct)}
                          helperText={
                            debtValidation.arrangementFeePct ||
                            financialHints.arrangementFeePct.helperText
                          }
                          fullWidth
                        />

                        <LabeledField
                          label="LLCR Covenant"
                          type="number"
                          value={projectDebt.llcrCovenant ?? ""}
                          onChange={updateProjectDebt("llcrCovenant")}
                          placeholder={financialHints.llcrCovenant.placeholder}
                          error={Boolean(debtValidation.llcrCovenant)}
                          helperText={
                            debtValidation.llcrCovenant || financialHints.llcrCovenant.helperText
                          }
                          fullWidth
                        />

                        <LabeledField
                          label="PLCR Covenant"
                          type="number"
                          value={projectDebt.plcrCovenant ?? ""}
                          onChange={updateProjectDebt("plcrCovenant")}
                          placeholder={financialHints.plcrCovenant.placeholder}
                          error={Boolean(debtValidation.plcrCovenant)}
                          helperText={
                            debtValidation.plcrCovenant || financialHints.plcrCovenant.helperText
                          }
                          fullWidth
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(projectDebt.cashSweepEnabled)}
                              onChange={updateProjectDebtBoolean("cashSweepEnabled")}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Cash Sweep
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {financialHints.cashSweepEnabled.helperText}
                              </Typography>
                            </Box>
                          }
                        />

                        {projectDebt.cashSweepEnabled && (
                          <LabeledField
                            label="Cash Sweep (%)"
                            type="number"
                            value={projectDebt.cashSweepPct ?? ""}
                            onChange={updateProjectDebt("cashSweepPct")}
                            placeholder={financialHints.cashSweepPct.placeholder}
                            error={Boolean(debtValidation.cashSweepPct)}
                            helperText={
                              debtValidation.cashSweepPct || financialHints.cashSweepPct.helperText
                            }
                            fullWidth
                          />
                        )}

                        <LabeledField
                          label="Refinancing Assumption"
                          select
                          value={projectDebt.refinancingAssumption}
                          onChange={updateProjectDebt("refinancingAssumption")}
                          helperText={financialHints.refinancingAssumption.helperText}
                          fullWidth
                        >
                          {REFINANCING_ASSUMPTION_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </LabeledField>

                        {(projectDebt.refinancingAssumption !== "not_modelled" ||
                          projectDebt.facilityType === "mini_perm") && (
                          <LabeledField
                            label="Refinancing Year"
                            type="number"
                            value={projectDebt.refinancingYear ?? ""}
                            onChange={updateProjectDebt("refinancingYear")}
                            placeholder={financialHints.refinancingYear.placeholder}
                            error={Boolean(debtValidation.refinancingYear)}
                            helperText={
                              debtValidation.refinancingYear ||
                              financialHints.refinancingYear.helperText
                            }
                            fullWidth
                          />
                        )}

                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(projectDebt.customDebtScheduleEnabled)}
                              onChange={updateProjectDebtBoolean("customDebtScheduleEnabled")}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Custom Debt Schedule
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {financialHints.customDebtScheduleEnabled.helperText}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Stack>
                  </Collapse>
                </Box>
              </SubSection>
            </Stack>
          </TabPanel>
        </Box>

      </Stack>


    </FeasibilityShell>
  );
}
